import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, Inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import {
	Tpl3plService,
	ExportQueueItem,
	Tpl3plQueueStatsCounts,
	Tpl3plSimulatedAvailabilityLine,
	Tpl3plSimulatedAvailabilityResponse,
	Tpl3plSplitBackorderLine,
	Tpl3plSplitBackorderPreviewResponse,
	Tpl3plSplitUninvoicedOpenLinesPreviewResponse
} from '../../../services/tpl3pl.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { OrdersService } from '../../../services/orders.service';
import { GlobalsService } from '../../../services/globals.service';
import { PrintService } from '../../../services/print.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';
import { FailedExportMissingSkusDialogComponent } from './failed-export-missing-skus-dialog.component';
import { AttachSavedCardToOrderDialogComponent } from '../tpl3pl-shipments/attach-saved-card-to-order-dialog.component';
import { Subscription, Subject, of, throwError, forkJoin, EMPTY } from 'rxjs';
import { catchError, concatMap, finalize, map, switchMap } from 'rxjs/operators';

@Component({
	selector: 'app-order-detail-dialog',
	template: `
		<div class="order-detail-dialog">
			<div class="dialog-header">
				<h2 class="dialog-title">
					Order <span class="order-no">{{ data.orderno }}</span>
					<span *ngIf="order?.header?.statusname" class="order-status-pill" [ngClass]="statusClass(order?.header?.orderstatusid)">
						{{ order.header.statusname }}
					</span>
				</h2>
				<button mat-icon-button (click)="close()" class="close-btn"><mat-icon>close</mat-icon></button>
			</div>
			<div class="dialog-body">
				<div *ngIf="loading" class="loading-state">
					<mat-spinner diameter="40"></mat-spinner>
					<p>Loading order…</p>
				</div>
				<div *ngIf="error && !loading" class="error-state">
					<mat-icon color="warn">error_outline</mat-icon>
					<p>{{ error }}</p>
				</div>
				<ng-container *ngIf="order && !loading">
					<div class="order-meta-row">
						<div class="meta-item" *ngIf="order.header?.shippername"><span class="meta-label">Ship Via</span><span>{{ order.header.shippername }}</span></div>
						<div class="meta-item" *ngIf="order.header?.datewanted"><span class="meta-label">Date Wanted</span><span>{{ order.header.datewanted }}</span></div>
						<div class="meta-item" *ngIf="order.header?.enteredby"><span class="meta-label">Entered By</span><span>{{ order.header.enteredby }}</span></div>
						<div class="meta-item" *ngIf="order.header?.date_created"><span class="meta-label">Created</span><span>{{ order.header.date_created }}</span></div>
					</div>
					<div class="order-address-row" *ngIf="shipToDisplay || order.header?.comments">
						<div class="addr-block" *ngIf="shipToDisplay">
							<div class="meta-label">Ship To</div>
							<div [innerHTML]="shipToDisplay"></div>
						</div>
						<div class="addr-block" *ngIf="order.header?.contactphone">
							<div class="meta-label">Phone</div>
							<div>{{ order.header.contactphone }}</div>
							<div *ngIf="order.header?.contactemail" class="mt-1">
								<div class="meta-label">Email</div>
								<div>{{ order.header.contactemail }}</div>
							</div>
						</div>
						<div class="addr-block" *ngIf="order.header?.comments">
							<div class="meta-label">Notes</div>
							<div>{{ order.header.comments }}</div>
						</div>
					</div>
					<div class="small text-muted mb-2" *ngIf="simulatedAvailabilityLoccode && order.details?.length">
						Avail. (sim.) uses fillability warehouse {{ simulatedAvailabilityLoccode }} and global queue simulation.
					</div>
					<table class="order-lines-table" *ngIf="order.details?.length">
						<thead>
							<tr>
								<th>Item</th>
								<th>Description</th>
								<th class="num">QOH</th>
								<th class="num" [matTooltip]="simulatedAvailableTooltip">Avail. (sim.)</th>
								<th class="num" [matTooltip]="fillableQtyTooltip">Fillable</th>
								<th class="num">Ordered</th>
								<th class="num">Invoiced</th>
								<th class="num">Outstanding</th>
								<th class="num">Price</th>
								<th class="num">Total</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let item of order.details">
								<td>{{ item.stkcode }}</td>
								<td>{{ item.description }}</td>
								<td class="num">{{ linePhysicalQohDisplay(item) }}</td>
								<td class="num">{{ lineSimAvailDisplay(item) }}</td>
								<td class="num">{{ lineFillableQty(item) }}</td>
								<td class="num">{{ item.ordered }}</td>
								<td class="num">{{ item.qtyinvoiced }}</td>
								<td class="num">{{ item.ordered - item.qtyinvoiced }}</td>
								<td class="num">{{ item.unitprice | currency }}</td>
								<td class="num">{{ lineExtendedPriceTotal(item) | currency }}</td>
							</tr>
						</tbody>
						<tfoot *ngIf="order.saved_totals?.length">
							<tr *ngFor="let t of order.saved_totals">
								<td colspan="8"></td>
								<td class="num total-label">{{ t.title }}:</td>
								<td class="num">{{ t.text | currency }}</td>
							</tr>
						</tfoot>
					</table>
				</ng-container>
			</div>
			<div class="dialog-footer">
				<button mat-stroked-button color="primary" type="button" (click)="openAttachSavedCard()" [disabled]="loading || !!error || !order">
					<mat-icon>credit_card</mat-icon>
					<span class="ml-1">Attach card &amp; pre-auth</span>
				</button>
				<button mat-stroked-button color="accent" type="button" (click)="reauthorizeCard()" [disabled]="reauthWorking || loading || !!error || !order">
					<mat-spinner *ngIf="reauthWorking" diameter="20" class="footer-spinner"></mat-spinner>
					<mat-icon *ngIf="!reauthWorking">credit_card</mat-icon>
					<span class="ml-1">Re-authorize card</span>
				</button>
				<button mat-stroked-button (click)="goToOrder()">
					<mat-icon>open_in_new</mat-icon> Open full order
				</button>
				<button mat-button (click)="close()">Close</button>
			</div>
		</div>
	`,
	styles: [`
		.order-detail-dialog { display: flex; flex-direction: column; height: 100%; }
		.dialog-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px 12px; border-bottom: 1px solid #dee2e6; }
		.dialog-title { margin: 0; font-size: 18px; font-weight: 600; display: flex; align-items: center; gap: 10px; }
		.order-no { color: #084c8c; }
		.close-btn { margin-left: auto; }
		.dialog-body { flex: 1; overflow-y: auto; padding: 16px 20px; }
		.dialog-footer { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex-wrap: wrap; padding: 10px 20px; border-top: 1px solid #dee2e6; }
		.footer-spinner { display: inline-block; vertical-align: middle; margin-right: 4px; }
		.loading-state, .error-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px; gap: 12px; color: #6c757d; }
		.order-meta-row { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid #f0f0f0; }
		.meta-item { display: flex; flex-direction: column; gap: 2px; min-width: 120px; }
		.meta-label { font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6c757d; letter-spacing: 0.4px; }
		.order-address-row { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid #f0f0f0; }
		.addr-block { min-width: 160px; font-size: 13px; }
		.order-lines-table { width: 100%; border-collapse: collapse; font-size: 13px; }
		.order-lines-table th { text-align: left; padding: 6px 8px; border-bottom: 2px solid #dee2e6; font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6c757d; white-space: nowrap; }
		.order-lines-table td { padding: 5px 8px; border-bottom: 1px solid #f0f0f0; }
		.order-lines-table .num { text-align: right; }
		.order-lines-table tfoot td { font-weight: 600; border-top: 2px solid #dee2e6; }
		.total-label { color: #495057; }
		.order-status-pill { display: inline-block; padding: 2px 9px; border-radius: 10px; font-size: 12px; font-weight: 500; border: 1px solid transparent; }
		.erp-status-open        { background: #e7f4ff; color: #084c8c; border-color: #b3ddff; }
		.erp-status-invoiced    { background: #e9f7ef; color: #0b5d1e; border-color: #bfe7cf; }
		.erp-status-cancelled   { background: #fdecea; color: #8a1c1c; border-color: #f5c2c0; }
		.erp-status-credit-hold { background: #fff3e0; color: #b34a00; border-color: #ffcc80; }
		.erp-status-preauth-authorized { background: #76ffa1; color: #0d0d0d; border-color: #3dcc6e; }
		.erp-status-preauth-decline    { background: #ff006c; color: #fff; border-color: #c2004f; }
		.erp-status-default     { background: #f1f3f5; color: #495057; border-color: #e9ecef; }
	`]
})
export class OrderDetailDialogComponent {
	order: any = null;
	/** Resolved ship-to for display (API shipto or built from deladd* when API HTML was empty). */
	shipToDisplay: SafeHtml | null = null;
	loading = true;
	error: string | null = null;
	simulatedAvailabilityLoccode = '';
	reauthWorking = false;

	constructor(
		public dialogRef: MatDialogRef<OrderDetailDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: { orderno: string },
		private ordersService: OrdersService,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private domSanitizer: DomSanitizer,
		private matDialog: MatDialog
	) {
		this.ordersService.getOrder(data.orderno).subscribe({
			next: (o: any) => {
				if (o && (!Array.isArray(o.details) || o.details.length === 0)) {
					this.ordersService.getInvoicedOrder(data.orderno).subscribe({
						next: (inv: any) => {
							this.order = (inv && (Array.isArray(inv.details) ? inv.details.length > 0 : inv.header)) ? inv : o;
							this.refreshShipToDisplay();
							this.loadSimulatedAvailability();
						},
						error: () => {
							this.order = o;
							this.refreshShipToDisplay();
							this.loadSimulatedAvailability();
						}
					});
				} else {
					this.order = o;
					this.refreshShipToDisplay();
					this.loadSimulatedAvailability();
				}
			},
			error: () => { this.error = 'Could not load order ' + data.orderno; this.loading = false; }
		});
	}

	private refreshShipToDisplay(): void {
		const h = this.order?.header;
		this.shipToDisplay = null;
		if (!h) {
			return;
		}
		const fromApi = String(h.shipto || '').trim();
		if (this.visibleTextFromHtml(fromApi).length > 0) {
			this.shipToDisplay = this.domSanitizer.bypassSecurityTrustHtml(fromApi);
			return;
		}
		const lines: string[] = [];
		const pushLine = (v: unknown) => {
			const s = String(v ?? '').trim();
			if (s) {
				lines.push(this.escapeHtmlForShipTo(s));
			}
		};
		pushLine(h.deliverto);
		pushLine(h.deladd1);
		pushLine(h.deladd2);
		const cityLine = [h.deladd3, h.deladd4, h.deladd5]
			.map((x: unknown) => String(x ?? '').trim())
			.filter(Boolean)
			.join(', ');
		if (cityLine) {
			lines.push(this.escapeHtmlForShipTo(cityLine));
		}
		if (lines.length > 0) {
			this.shipToDisplay = this.domSanitizer.bypassSecurityTrustHtml(lines.join('<br />'));
		}
	}

	private visibleTextFromHtml(html: string): string {
		if (!html) {
			return '';
		}
		const withNl = html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ' ');
		return withNl.replace(/\s+/g, ' ').trim();
	}

	private escapeHtmlForShipTo(s: string): string {
		return s
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private loadSimulatedAvailability(): void {
		this.tpl3plService.getSimulatedLineAvailability(this.data.orderno).subscribe({
			next: (sim: Tpl3plSimulatedAvailabilityResponse) => {
				this.applySimulatedAvailability(sim);
				this.loading = false;
			},
			error: () => { this.loading = false; }
		});
	}

	get simulatedAvailableTooltip(): string {
		const warehouse = this.simulatedAvailabilityLoccode ? this.simulatedAvailabilityLoccode : 'fillability warehouse';
		return 'Simulated remaining QOH when this line is reached in the global open-order queue walk (' + warehouse + ').';
	}

	get fillableQtyTooltip(): string {
		return 'How much of this line can ship now: min(outstanding, avail. sim.) at the fillability warehouse.';
	}

	linePhysicalQohDisplay(item: { qoh?: number | null }): number {
		if (item == null || item.qoh == null || (typeof item.qoh === 'number' && Number.isNaN(item.qoh))) {
			return 0;
		}
		return Math.max(0, Number(item.qoh));
	}

	lineSimAvailDisplay(item: { simulated_available?: number | null }): number {
		if (item == null || item.simulated_available == null) {
			return 0;
		}
		const n = Number(item.simulated_available);
		if (Number.isNaN(n)) {
			return 0;
		}
		return n;
	}

	lineFillableQty(item: { ordered?: number; qtyinvoiced?: number; simulated_available?: number | null }): number {
		if (item == null || item.simulated_available == null) {
			return 0;
		}
		const ordered = Number(item.ordered);
		const inv = Number(item.qtyinvoiced);
		const out = ordered - inv;
		const sim = Number(item.simulated_available);
		if (Number.isNaN(out) || Number.isNaN(sim)) {
			return 0;
		}
		return Math.min(out, Math.max(0, sim));
	}

	/**
	 * Extended line $ (qty × net unit). API may omit discountprice; in JS `null * qty` is 0, which wrongly shows $0 totals.
	 * Matches orders/open.php: net unit = unitprice − round(unitprice × discountpercent, 2).
	 */
	lineExtendedPriceTotal(item: { ordered?: unknown; unitprice?: unknown; discountpercent?: unknown; discountprice?: unknown }): number {
		if (item == null) {
			return 0;
		}
		const qty = Number(item.ordered);
		if (!Number.isFinite(qty) || qty === 0) {
			return 0;
		}
		const dp = item.discountprice;
		if (dp != null && dp !== '' && Number.isFinite(Number(dp))) {
			return Math.round(Number(dp) * qty * 100) / 100;
		}
		const unit = Number(item.unitprice);
		if (!Number.isFinite(unit)) {
			return 0;
		}
		const disc = Number(item.discountpercent ?? 0);
		const discAmt = Math.round(unit * disc * 100) / 100;
		const netUnit = Math.round((unit - discAmt) * 100) / 100;
		return Math.round(netUnit * qty * 100) / 100;
	}

	private applySimulatedAvailability(sim: Tpl3plSimulatedAvailabilityResponse): void {
		if (!sim || !sim.success || !this.order || !Array.isArray(this.order.details)) {
			return;
		}
		this.simulatedAvailabilityLoccode = sim.fillability_loccode || '';
		const lineMap: { [key: string]: Tpl3plSimulatedAvailabilityLine } = {};
		for (const line of (sim.lines || [])) {
			const k = this.lineKey(line.orderlineno, line.stkcode);
			lineMap[k] = line;
		}
		for (const detail of this.order.details) {
			const k = this.lineKey(detail.orderlineno, detail.stkcode);
			const simLine = lineMap[k];
			if (simLine) {
				detail.simulated_available = simLine.simulated_available;
			}
		}
	}

	private lineKey(orderlineno: any, stkcode: any): string {
		const line = String(orderlineno == null ? '' : orderlineno).trim();
		const sku = String(stkcode == null ? '' : stkcode).trim();
		return line + '|' + sku;
	}

	statusClass(id: any): string {
		const n = parseInt(id, 10);
		if (isNaN(n)) { return 'erp-status-default'; }
		if (n === 80) { return 'erp-status-credit-hold'; }
		if (n === 53) { return 'erp-status-preauth-authorized'; }
		if (n === 52) { return 'erp-status-preauth-decline'; }
		if (n >= 90) { return 'erp-status-cancelled'; }
		if (n > 29 && n < 51) { return 'erp-status-invoiced'; }
		if (n <= 29) { return 'erp-status-open'; }
		return 'erp-status-default';
	}

	goToOrder(): void {
		this.dialogRef.close();
		this.router.navigate(['/orders/order-inquiry'], { queryParams: { orderno: this.data.orderno } });
	}

	openAttachSavedCard(): void {
		const on = (this.data?.orderno || '').trim();
		if (!on || !this.order) {
			return;
		}
		const h = this.order.header || {};
		const custName = String(h.deliverto || (h as { customername?: string }).customername || '').trim();
		this.matDialog
			.open(AttachSavedCardToOrderDialogComponent, {
				width: '460px',
				maxWidth: '96vw',
				data: {
					orderno: on,
					customerName: custName,
					tpl3plService: this.tpl3plService,
					runPreauthAfterAttach: true,
				},
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.success && r.message) {
					this.globalSearchService.showNotification(
						r.message,
						r.preauth_ok === false ? 'warning' : 'success',
						'bottom',
						'right'
					);
				}
			});
	}

	reauthorizeCard(): void {
		const on = (this.data?.orderno || '').trim();
		if (!on || this.reauthWorking) {
			return;
		}
		this.reauthWorking = true;
		this.tpl3plService.reauthorizePreExport(on).subscribe({
			next: (result) => {
				this.reauthWorking = false;
				const ok = result && typeof result === 'object' && result.success;
				this.globalSearchService.showNotification(
					result?.message || (ok ? 'Card re-authorized' : 'Re-authorization failed'),
					ok ? 'success' : 'warning',
					'bottom',
					'right'
				);
			},
			error: (err) => {
				this.reauthWorking = false;
				const msg = err?.error?.message || err?.message || 'Re-authorization request failed';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
			}
		});
	}

	close(): void { this.dialogRef.close(); }
}

export interface ForceErpStatusDialogData {
	orderno: string;
	currentStatusId: string;
	statuses: Array<{ order_status_id: number | string; name: string }>;
}

@Component({
	selector: 'app-force-erp-status-dialog',
	template: `
		<h2 mat-dialog-title>Set ERP order status</h2>
		<mat-dialog-content>
			<p class="small text-muted mb-2">Order <span class="text-monospace">{{ data.orderno }}</span></p>
			<p class="small text-muted mb-3">
				Updates <code>salesorders.orderstatusid</code> only. This does not run dispatch routes, picking-freeze, or packing-slip logic.
			</p>
			<label class="force-erp-status-label" for="force-erp-status-select">Status</label>
			<select
				id="force-erp-status-select"
				class="form-control force-erp-status-select"
				[(ngModel)]="selectedId"
				name="forceErpStatusId">
				<option *ngFor="let s of data.statuses" [ngValue]="toNum(s.order_status_id)">
					{{ s.name }} ({{ s.order_status_id }})
				</option>
			</select>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Cancel</button>
			<button mat-raised-button color="primary" type="button" (click)="save()" [disabled]="selectedId == null || selectedId <= 0 || saving">
				<mat-spinner *ngIf="saving" diameter="20" class="d-inline-block mr-1"></mat-spinner>
				{{ saving ? 'Saving…' : 'Save' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [`
		code { font-size: 12px; }
		.force-erp-status-label {
			display: block;
			font-size: 12px;
			font-weight: 600;
			text-transform: uppercase;
			color: #6c757d;
			margin-bottom: 6px;
			letter-spacing: 0.03em;
		}
		.force-erp-status-select {
			width: 100%;
			max-width: 100%;
			padding: 10px 12px;
			font-size: 14px;
			line-height: 1.4;
			border: 1px solid #ced4da;
			border-radius: 4px;
			background-color: #fff;
			cursor: pointer;
		}
	`]
})
export class ForceErpStatusDialogComponent {
	selectedId: number | null = null;
	saving = false;

	constructor(
		public dialogRef: MatDialogRef<ForceErpStatusDialogComponent, { success: boolean; erp_order_status_id?: string; erp_order_status_name?: string } | undefined>,
		@Inject(MAT_DIALOG_DATA) public data: ForceErpStatusDialogData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {
		const n = parseInt(String(data.currentStatusId || '').trim(), 10);
		this.selectedId = isNaN(n) ? null : n;
	}

	toNum(v: number | string): number {
		const n = parseInt(String(v), 10);
		return isNaN(n) ? 0 : n;
	}

	save(): void {
		if (this.selectedId == null || this.selectedId <= 0 || !this.data.orderno) {
			return;
		}
		this.saving = true;
		this.tpl3plService.setErpOrderStatus(this.data.orderno, this.selectedId).subscribe({
			next: (r) => {
				this.saving = false;
				if (r.success) {
					this.globalSearchService.showNotification(r.message || 'ERP status updated', 'success', 'bottom', 'right');
					this.dialogRef.close({
						success: true,
						erp_order_status_id: r.erp_order_status_id,
						erp_order_status_name: r.erp_order_status_name,
					});
				} else {
					this.globalSearchService.showNotification(r.message || 'Could not update status', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.saving = false;
				this.globalSearchService.showNotification('Request failed', 'danger', 'bottom', 'right');
			}
		});
	}
}

/** Parse pasted CSV / list: one order number per line, or first column of comma/tab/semicolon-separated rows. */
export function parseWarehouseCancelOrderListInput(raw: string): string[] {
	if (raw == null || typeof raw !== 'string') {
		return [];
	}
	const text = raw.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
	const seen = new Set<string>();
	const out: string[] = [];
	const headerRe = /^(orderno|order\s*#|order\s*number|so\s*#|sales\s*order|order\s*no\.?)$/i;
	for (const line of text.split('\n')) {
		const trimmed = line.trim();
		if (!trimmed) {
			continue;
		}
		let token = trimmed;
		if (/[,\t;]/.test(trimmed)) {
			const parts = trimmed
				.split(/[,\t;]+/)
				.map((p) => p.trim().replace(/^["']|["']$/g, ''))
				.filter((p) => !!p);
			token = parts.length ? parts[0] : '';
		} else {
			token = trimmed.replace(/^["']|["']$/g, '');
		}
		if (!token) {
			continue;
		}
		if (headerRe.test(token)) {
			continue;
		}
		if (!seen.has(token)) {
			seen.add(token);
			out.push(token);
		}
	}
	return out;
}

@Component({
	selector: 'app-csv-warehouse-cancel-dialog',
	template: `
		<h2 mat-dialog-title>Cancel at warehouse from CSV / list</h2>
		<mat-dialog-content class="csv-wh-cancel-dialog">
			<p class="small text-muted mb-2">
				Paste one sales order number per line, or CSV / TSV (first column is used). Rows matching a header name like
				<code>orderno</code> are skipped. ERP is not changed — each number is sent to the 3PL cancel API, same as
				<strong>Cancel at warehouse</strong> on checked rows.
			</p>
			<textarea
				class="form-control csv-wh-cancel-textarea"
				rows="14"
				[(ngModel)]="pasteText"
				(ngModelChange)="onPasteChange()"
				placeholder="1654321&#10;1654322&#10;1654323"></textarea>
			<div class="mt-2 mb-2">
				<label class="csv-wh-file-label mb-1">Or load a .csv / .txt file</label>
				<input #fileIn type="file" accept=".csv,.txt,text/csv,text/plain" (change)="onFileSelected($event)" class="d-block" />
			</div>
			<p class="small mb-0" *ngIf="parsedOrdernos.length">
				<strong>{{ parsedOrdernos.length }}</strong> unique order number(s). Preview:
				<span class="text-monospace">{{ previewSnippet }}</span>
			</p>
			<p class="small text-warning mb-0" *ngIf="pasteText && !parsedOrdernos.length">No valid order numbers parsed — check format.</p>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Close</button>
			<button
				mat-raised-button
				color="warn"
				type="button"
				(click)="confirmAndClose()"
				[disabled]="parsedOrdernos.length === 0">
				Cancel {{ parsedOrdernos.length || '…' }} at warehouse
			</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.csv-wh-cancel-dialog {
				min-width: 280px;
				max-width: 640px;
			}
			.csv-wh-cancel-textarea {
				width: 100%;
				font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
				font-size: 13px;
			}
			.csv-wh-file-label {
				display: block;
				font-size: 12px;
				font-weight: 600;
				text-transform: uppercase;
				color: #6c757d;
				letter-spacing: 0.03em;
			}
		`,
	],
})
export class CsvWarehouseCancelDialogComponent {
	pasteText = '';

	constructor(public dialogRef: MatDialogRef<CsvWarehouseCancelDialogComponent, { ordernos: string[] } | undefined>) {}

	get parsedOrdernos(): string[] {
		return parseWarehouseCancelOrderListInput(this.pasteText);
	}

	get previewSnippet(): string {
		const list = this.parsedOrdernos;
		if (!list.length) {
			return '';
		}
		const max = 8;
		const head = list.slice(0, max).join(', ');
		return list.length > max ? head + ', …' : head;
	}

	onPasteChange(): void {}

	onFileSelected(ev: Event): void {
		const input = ev.target as HTMLInputElement;
		const file = input.files && input.files[0];
		if (!file) {
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			const t = typeof reader.result === 'string' ? reader.result : '';
			this.pasteText = t;
			input.value = '';
		};
		reader.readAsText(file);
	}

	confirmAndClose(): void {
		const list = this.parsedOrdernos;
		if (!list.length) {
			return;
		}
		const msg =
			'Cancel ' +
			list.length +
			' order(s) at the warehouse (3PL) only?\n\n' +
			'The ERP will not be changed. Use when sales orders are already handled in the ERP but the outbound is still open at the warehouse.';
		if (!window.confirm(msg)) {
			return;
		}
		this.dialogRef.close({ ordernos: list });
	}
}

export interface SplitBackorderDialogData {
	orderno: string;
	/** Queue row eligible for bump-based full pathway */
	showFullResubmitPathway?: boolean;
	/** Parent runs bump → split → export pipeline */
	onFullResubmitPathway?: (orderno: string) => void;
}

@Component({
	selector: 'app-split-backorder-dialog',
	template: `
		<h2 mat-dialog-title>Split partial order → back order</h2>
		<mat-dialog-content class="split-bo-dialog-content">
			<p class="small text-muted mb-2">Order <span class="text-monospace">{{ data.orderno }}</span></p>
			<div *ngIf="loading" class="d-flex align-items-center py-3">
				<mat-spinner diameter="32" class="mr-2"></mat-spinner>
				<span>Loading preview…</span>
			</div>
			<ng-container *ngIf="!loading && preview">
				<div class="alert alert-success small mb-2" *ngIf="preview.split_executed && preview.split_backorder_orderno">
					<strong>Already split.</strong> Back-order SO
					<span class="text-monospace">{{ preview.split_backorder_orderno }}</span>
					was created from this order (see order notes).
				</div>
				<div class="alert alert-secondary small mb-2" *ngIf="preview.split_parent_orderno">
					<strong>Back-order from split.</strong> Parent SO
					<span class="text-monospace">{{ preview.split_parent_orderno }}</span>
				</div>
				<div class="alert alert-warning small mb-2" *ngIf="preview.relaxed_fillability_bucket">
					<strong>PGS back-order / cancel path.</strong> Split is allowed even though fillability is not “partial”. The source order must still keep at least one 3PL line to re-export.
				</div>
				<div class="alert alert-warning small" *ngIf="preview.picking_blocked">{{ preview.message || 'Split is blocked.' }}</div>
				<div class="alert alert-warning small" *ngIf="preview.tree_blocked">{{ preview.message }}</div>
				<div class="alert alert-info small" *ngIf="preview.success && preview.split_applicable && preview.no_op">{{ preview.message }}</div>
				<div class="alert alert-secondary small" *ngIf="preview.success && !preview.split_applicable">{{ preview.message }}</div>
				<div class="small mb-2" *ngIf="preview.fillability_loccode">
					Fillability warehouse: <span class="text-monospace">{{ preview.fillability_loccode }}</span>
				</div>
				<div class="alert alert-info small mb-2" *ngIf="splitPreviewHasKitHeaderLines(preview)">
					<strong>Kit headers (mbflag K)</strong> on this order: simulated availability is set from each line’s outstanding quantity and
					<strong>does not</strong> reduce the SKU’s shared simulation pool, so multiple K lines on the same SKU stay fillable. QOH is informational.
				</div>
				<div *ngIf="(preview.lines_move?.length || 0) > 0">
					<h4 class="h6 mt-2">Lines that would move (new back-order SO)</h4>
					<table class="table table-sm table-bordered split-bo-table">
						<thead><tr><th>Line</th><th>SKU</th><th>Description</th><th>Type</th><th class="text-right" [matTooltip]="splitPreviewQohTooltip">QOH</th><th class="text-right">Outstanding</th><th class="text-right" [matTooltip]="splitPreviewSimAvailTooltip">Sim. avail.</th><th class="text-right" matTooltip="min(outstanding, sim. avail.)">Fillable</th></tr></thead>
						<tbody>
							<tr *ngFor="let l of preview.lines_move">
								<td>{{ l.orderlineno }}</td>
								<td class="text-monospace">{{ l.stkcode }}</td>
								<td class="split-bo-desc-cell">{{ l.description || '—' }}</td>
								<td class="split-bo-type-cell">
									<span class="text-monospace">{{ l.mbflag || '—' }}</span>
									<span *ngIf="l.fillability_kit_header" class="split-bo-kit-pill">Kit</span>
								</td>
								<td class="text-right">{{ splitLineQohDisplay(l) }}</td>
								<td class="text-right">{{ l.remaining }}</td>
								<td class="text-right" [matTooltip]="splitLineSimAvailRowTooltip(l)">{{ splitSimAvailDisplay(l) }}</td>
								<td class="text-right">{{ l.fillable_qty != null ? l.fillable_qty : '—' }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div *ngIf="(preview.lines_stay?.length || 0) > 0">
					<h4 class="h6 mt-3">Lines staying on this order</h4>
					<table class="table table-sm table-bordered split-bo-table">
						<thead><tr><th>Line</th><th>SKU</th><th>Description</th><th>Type</th><th class="text-right" [matTooltip]="splitPreviewQohTooltip">QOH</th><th class="text-right">Outstanding</th><th class="text-right" [matTooltip]="splitPreviewSimAvailTooltip">Sim. avail.</th><th class="text-right" matTooltip="min(outstanding, sim. avail.)">Fillable</th></tr></thead>
						<tbody>
							<tr *ngFor="let l of preview.lines_stay">
								<td>{{ l.orderlineno }}</td>
								<td class="text-monospace">{{ l.stkcode }}</td>
								<td class="split-bo-desc-cell">{{ l.description || '—' }}</td>
								<td class="split-bo-type-cell">
									<span class="text-monospace">{{ l.mbflag || '—' }}</span>
									<span *ngIf="l.fillability_kit_header" class="split-bo-kit-pill">Kit</span>
								</td>
								<td class="text-right">{{ splitLineQohDisplay(l) }}</td>
								<td class="text-right">{{ l.remaining }}</td>
								<td class="text-right" [matTooltip]="splitLineSimAvailRowTooltip(l)">{{ splitSimAvailDisplay(l) }}</td>
								<td class="text-right">{{ l.fillable_qty != null ? l.fillable_qty : '—' }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="small text-muted mt-2 mb-0" *ngIf="preview.split_applicable && !preview.no_op && (preview.lines_move?.length || 0) > 0">
					Freight on the new SO is set to 0. Only lines that are short at the fillability warehouse move; other open lines stay on this order. Original header totals may need manual reconciliation.
				</p>
				<div class="alert alert-danger small" *ngIf="!preview.success && !preview.picking_blocked && !preview.tree_blocked">{{ preview.message || 'Preview failed' }}</div>
				<div class="alert alert-info small mb-0 mt-2" *ngIf="data.showFullResubmitPathway && data.onFullResubmitPathway && !loading">
					<strong>Full resubmit pathway</strong> (same as the queue column): PGS cancel when your integration is PGS, next SO #, split short lines if applicable, export fillable quantity to the 3PL, then ERP status follows <em>Status when exported to 3PL</em> in config after a successful export.
				</div>
			</ng-container>
		</mat-dialog-content>
		<mat-dialog-actions align="end" class="split-bo-dialog-actions flex-wrap">
			<button mat-button type="button" mat-dialog-close>Close</button>
			<button
				mat-stroked-button
				color="accent"
				type="button"
				*ngIf="data.showFullResubmitPathway && data.onFullResubmitPathway"
				(click)="runFullPathwayFromDialog()"
				[disabled]="loading || executing || (preview && (preview.picking_blocked || preview.tree_blocked))">
				Full pathway: cancel / bump → split → export
			</button>
			<button mat-raised-button color="primary" type="button" (click)="execute()" [disabled]="!canExecute || executing">
				<mat-spinner *ngIf="executing" diameter="20" class="d-inline-block mr-1"></mat-spinner>
				{{ executing ? 'Working…' : 'Execute split' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [`
		.split-bo-dialog-content { min-width: 720px; max-width: 960px; max-height: 70vh; overflow: auto; }
		.split-bo-table { font-size: 13px; }
		.split-bo-desc-cell { max-width: 260px; white-space: normal; word-break: break-word; vertical-align: top; }
		.split-bo-type-cell { white-space: nowrap; }
		.split-bo-type-cell .split-bo-kit-pill {
			display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px;
			font-size: 10px; font-weight: 600; text-transform: uppercase; vertical-align: middle;
			background: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb;
		}
		.split-bo-dialog-actions { gap: 8px; justify-content: flex-end; }
	`]
})
export class SplitBackorderDialogComponent implements OnInit {
	loading = true;
	executing = false;
	preview: Tpl3plSplitBackorderPreviewResponse | null = null;

	get splitPreviewQohTooltip(): string {
		const w = this.preview?.fillability_loccode ? this.preview.fillability_loccode : 'fillability warehouse';
		return 'Physical QOH (SUM locstock.quantity) at ' + w + '.';
	}

	get splitPreviewSimAvailTooltip(): string {
		return 'Simulated remaining quantity when this line is reached in the global open-order queue walk at the fillability warehouse. Kit headers (K) do not consume the SKU pool.';
	}

	splitPreviewHasKitHeaderLines(p: Tpl3plSplitBackorderPreviewResponse | null): boolean {
		if (!p) {
			return false;
		}
		const lines = [...(p.lines_stay || []), ...(p.lines_move || [])];
		return lines.some((l) => l.fillability_kit_header === true);
	}

	splitLineSimAvailRowTooltip(l: Tpl3plSplitBackorderLine): string {
		if (l.fillability_kit_header) {
			return 'Kit header: sim. avail. matches this line’s outstanding and does not decrement the shared SKU simulation pool.';
		}
		return this.splitPreviewSimAvailTooltip;
	}

	splitLineQohDisplay(l: { qoh?: number | null }): number {
		if (l == null || l.qoh == null || (typeof l.qoh === 'number' && Number.isNaN(l.qoh))) {
			return 0;
		}
		return Math.max(0, Number(l.qoh));
	}

	splitSimAvailDisplay(l: { simulated_available?: number | null }): number {
		if (l == null || l.simulated_available == null) {
			return 0;
		}
		const n = Number(l.simulated_available);
		if (Number.isNaN(n)) {
			return 0;
		}
		return n;
	}

	constructor(
		public dialogRef: MatDialogRef<SplitBackorderDialogComponent, { success: boolean; new_orderno?: string | null } | undefined>,
		@Inject(MAT_DIALOG_DATA) public data: SplitBackorderDialogData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {}

	ngOnInit(): void {
		this.tpl3plService.splitBackorderPreview(this.data.orderno).subscribe({
			next: (p) => {
				this.preview = p;
				this.loading = false;
			},
			error: () => {
				this.preview = { success: false, message: 'Preview request failed' };
				this.loading = false;
			}
		});
	}

	get canExecute(): boolean {
		const p = this.preview;
		if (!p || !p.success || !p.split_applicable || p.no_op) {
			return false;
		}
		const n = p.lines_move?.length ?? 0;
		return n > 0;
	}

	runFullPathwayFromDialog(): void {
		const fn = this.data.onFullResubmitPathway;
		const on = (this.data.orderno || '').trim();
		if (!fn || !on) {
			return;
		}
		this.dialogRef.close();
		fn(on);
	}

	execute(): void {
		if (!this.canExecute || this.executing || !this.data.orderno) {
			return;
		}
		this.executing = true;
		this.tpl3plService.splitBackorderExecute(this.data.orderno).subscribe({
			next: (r) => {
				this.executing = false;
				if (r.success) {
					const msg = r.new_orderno
						? `Back order SO ${r.new_orderno} created (${r.moved_line_count ?? 0} line(s)).`
						: (r.message || 'Split complete.');
					this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
					this.dialogRef.close({ success: true, new_orderno: r.new_orderno ?? null });
				} else {
					this.globalSearchService.showNotification(r.message || 'Split failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.executing = false;
				this.globalSearchService.showNotification('Split request failed', 'danger', 'bottom', 'right');
			}
		});
	}
}

export interface CancelAndSplitBackorderDialogData {
	orderno: string;
	/** From queue row: exported at 3PL, not shipped/invoiced — enables warehouse cancel actions. */
	canCancelWarehouse: boolean;
}

/**
 * Preview PGS warehouse DELETE + partial back-order split, then run cancel (ERP unchanged) and optionally execute split.
 */
@Component({
	selector: 'app-cancel-and-split-backorder-dialog',
	template: `
		<h2 mat-dialog-title>Preview: cancel at warehouse &amp; split</h2>
		<mat-dialog-content class="cas-bo-dialog-content">
			<p class="small text-muted mb-2">Order <span class="text-monospace">{{ data.orderno }}</span></p>
			<div *ngIf="loading" class="d-flex align-items-center py-3">
				<mat-spinner diameter="32" class="mr-2"></mat-spinner>
				<span>Loading cancel + split previews…</span>
			</div>
			<ng-container *ngIf="!loading">
				<mat-accordion multi="true">
					<mat-expansion-panel [expanded]="true">
						<mat-expansion-panel-header>
							<mat-panel-title>1. Warehouse cancel (PGS)</mat-panel-title>
							<mat-panel-description class="cas-bo-panel-desc">
								<span *ngIf="cancelPreview?.success" class="text-success">Preview ready</span>
								<span *ngIf="cancelPreview && !cancelPreview.success" class="text-danger">Unavailable</span>
							</mat-panel-description>
						</mat-expansion-panel-header>
						<p class="small mb-2" *ngIf="!canCancelWarehouse">
							This order is not in a state for warehouse-only cancel from the queue (typically need
							<strong>exported</strong> and not shipped/invoiced at 3PL). Split preview below may still apply.
						</p>
						<ng-container *ngIf="cancelPreview?.success">
							<p class="small mb-1">
								Channel: <span class="text-monospace">{{ cancelPreview.channel || 'auto' }}</span> — on confirm, the API sends
								<code>DELETE</code> to PGS, then resets the export queue row to <strong>pending</strong> (same as standalone warehouse cancel).
							</p>
							<pre class="cas-bo-json">{{ cancelPreview.request_payload | json }}</pre>
						</ng-container>
						<p class="small text-danger mb-0" *ngIf="cancelPreview && !cancelPreview.success">{{ cancelPreview.message || 'Cancel preview failed' }}</p>
					</mat-expansion-panel>
					<mat-expansion-panel [expanded]="true">
						<mat-expansion-panel-header>
							<mat-panel-title>2. Partial split → back order</mat-panel-title>
							<mat-panel-description class="cas-bo-panel-desc">
								<span *ngIf="splitPreview?.success && splitPreview.split_applicable && (splitPreview.lines_move?.length || 0) > 0" class="text-success">
									{{ splitPreview.lines_move?.length }} line(s) would move
								</span>
								<span *ngIf="splitPreview?.success && (!splitPreview.split_applicable || splitPreview.no_op || !(splitPreview.lines_move?.length))" class="text-muted">No split / no-op</span>
								<span *ngIf="splitPreview && !splitPreview.success" class="text-danger">Preview issue</span>
							</mat-panel-description>
						</mat-expansion-panel-header>
						<ng-container *ngIf="splitPreview">
							<div class="alert alert-success small mb-2" *ngIf="splitPreview.split_executed && splitPreview.split_backorder_orderno">
								<strong>Already split.</strong> Back-order SO
								<span class="text-monospace">{{ splitPreview.split_backorder_orderno }}</span>.
							</div>
							<div class="alert alert-secondary small mb-2" *ngIf="splitPreview.split_parent_orderno">
								<strong>Back-order from split.</strong> Parent SO
								<span class="text-monospace">{{ splitPreview.split_parent_orderno }}</span>
							</div>
							<div class="alert alert-warning small mb-2" *ngIf="splitPreview.relaxed_fillability_bucket">
								<strong>PGS back-order / cancel path.</strong> Split allowed without “partial” fillability; source must keep at least one 3PL line to re-export.
							</div>
							<div class="alert alert-warning small" *ngIf="splitPreview.picking_blocked">{{ splitPreview.message || 'Split is blocked.' }}</div>
							<div class="alert alert-warning small" *ngIf="splitPreview.tree_blocked">{{ splitPreview.message }}</div>
							<div class="alert alert-info small" *ngIf="splitPreview.success && splitPreview.split_applicable && splitPreview.no_op">{{ splitPreview.message }}</div>
							<div class="alert alert-secondary small" *ngIf="splitPreview.success && !splitPreview.split_applicable">{{ splitPreview.message }}</div>
							<div class="small mb-2" *ngIf="splitPreview.fillability_loccode">
								Fillability warehouse: <span class="text-monospace">{{ splitPreview.fillability_loccode }}</span>
							</div>
							<div class="alert alert-info small mb-2" *ngIf="splitPreviewHasKitHeaderLines(splitPreview)">
								<strong>Kit headers (mbflag K)</strong> — simulated availability does not reduce the shared SKU pool for multiple K lines.
							</div>
							<div *ngIf="(splitPreview.lines_move?.length || 0) > 0">
								<h4 class="h6 mt-2">Lines that would move (new back-order SO)</h4>
								<table class="table table-sm table-bordered split-bo-table">
									<thead>
										<tr>
											<th>Line</th>
											<th>SKU</th>
											<th>Description</th>
											<th>Type</th>
											<th class="text-right" [matTooltip]="splitPreviewQohTooltip">QOH</th>
											<th class="text-right">Outstanding</th>
											<th class="text-right" [matTooltip]="splitPreviewSimAvailTooltip">Sim. avail.</th>
											<th class="text-right" matTooltip="min(outstanding, sim. avail.)">Fillable</th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let l of splitPreview.lines_move">
											<td>{{ l.orderlineno }}</td>
											<td class="text-monospace">{{ l.stkcode }}</td>
											<td class="split-bo-desc-cell">{{ l.description || '—' }}</td>
											<td class="split-bo-type-cell">
												<span class="text-monospace">{{ l.mbflag || '—' }}</span>
												<span *ngIf="l.fillability_kit_header" class="split-bo-kit-pill">Kit</span>
											</td>
											<td class="text-right">{{ splitLineQohDisplay(l) }}</td>
											<td class="text-right">{{ l.remaining }}</td>
											<td class="text-right" [matTooltip]="splitLineSimAvailRowTooltip(l)">{{ splitSimAvailDisplay(l) }}</td>
											<td class="text-right">{{ l.fillable_qty != null ? l.fillable_qty : '—' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div *ngIf="(splitPreview.lines_stay?.length || 0) > 0">
								<h4 class="h6 mt-3">Lines staying on this order</h4>
								<table class="table table-sm table-bordered split-bo-table">
									<thead>
										<tr>
											<th>Line</th>
											<th>SKU</th>
											<th>Description</th>
											<th>Type</th>
											<th class="text-right" [matTooltip]="splitPreviewQohTooltip">QOH</th>
											<th class="text-right">Outstanding</th>
											<th class="text-right" [matTooltip]="splitPreviewSimAvailTooltip">Sim. avail.</th>
											<th class="text-right" matTooltip="min(outstanding, sim. avail.)">Fillable</th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let l of splitPreview.lines_stay">
											<td>{{ l.orderlineno }}</td>
											<td class="text-monospace">{{ l.stkcode }}</td>
											<td class="split-bo-desc-cell">{{ l.description || '—' }}</td>
											<td class="split-bo-type-cell">
												<span class="text-monospace">{{ l.mbflag || '—' }}</span>
												<span *ngIf="l.fillability_kit_header" class="split-bo-kit-pill">Kit</span>
											</td>
											<td class="text-right">{{ splitLineQohDisplay(l) }}</td>
											<td class="text-right">{{ l.remaining }}</td>
											<td class="text-right" [matTooltip]="splitLineSimAvailRowTooltip(l)">{{ splitSimAvailDisplay(l) }}</td>
											<td class="text-right">{{ l.fillable_qty != null ? l.fillable_qty : '—' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
							<p class="small text-muted mt-2 mb-0" *ngIf="splitPreview.split_applicable && !splitPreview.no_op && (splitPreview.lines_move?.length || 0) > 0">
								Freight on the new SO is set to 0. After a successful warehouse cancel, the split runs on this SO number (lines unchanged until then).
							</p>
							<div class="alert alert-danger small mb-0" *ngIf="!splitPreview.success && !splitPreview.picking_blocked && !splitPreview.tree_blocked">
								{{ splitPreview.message || 'Split preview failed' }}
							</div>
						</ng-container>
					</mat-expansion-panel>
				</mat-accordion>
				<div class="alert alert-secondary small mt-3 mb-0" *ngIf="executingStep">
					<mat-spinner diameter="18" class="d-inline-block mr-2 align-middle"></mat-spinner>
					{{ executingStep }}
				</div>
			</ng-container>
		</mat-dialog-content>
		<mat-dialog-actions align="end" class="cas-bo-actions flex-wrap">
			<button mat-button type="button" mat-dialog-close [disabled]="executing">Close</button>
			<button
				mat-stroked-button
				color="warn"
				type="button"
				(click)="runCancelWarehouseOnly()"
				[disabled]="loading || executing || !canRunWarehouseCancel">
				{{ executing && executingMode === 'cancel' ? 'Working…' : 'Cancel at warehouse only' }}
			</button>
			<button
				mat-raised-button
				color="warn"
				type="button"
				(click)="runCancelWarehouseThenSplit()"
				[disabled]="loading || executing || !canRunCancelAndSplit">
				{{ executing && executingMode === 'both' ? 'Working…' : 'Cancel at warehouse &amp; split' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.cas-bo-dialog-content {
				min-width: 320px;
				max-width: 960px;
				max-height: 75vh;
				overflow: auto;
			}
			.cas-bo-json {
				font-size: 12px;
				max-height: 200px;
				overflow: auto;
				background: #f8f9fa;
				border: 1px solid #dee2e6;
				border-radius: 4px;
				padding: 10px;
				margin: 0;
			}
			.cas-bo-panel-desc {
				flex-grow: 1;
				justify-content: flex-end;
			}
			.cas-bo-actions {
				gap: 8px;
				justify-content: flex-end;
			}
			.split-bo-table {
				font-size: 13px;
			}
			.split-bo-desc-cell {
				max-width: 220px;
				white-space: normal;
				word-break: break-word;
				vertical-align: top;
			}
			.split-bo-type-cell {
				white-space: nowrap;
			}
			.split-bo-type-cell .split-bo-kit-pill {
				display: inline-block;
				margin-left: 6px;
				padding: 1px 6px;
				border-radius: 4px;
				font-size: 10px;
				font-weight: 600;
				text-transform: uppercase;
				vertical-align: middle;
				background: #d1ecf1;
				color: #0c5460;
				border: 1px solid #bee5eb;
			}
		`,
	],
})
export class CancelAndSplitBackorderDialogComponent implements OnInit {
	loading = true;
	executing = false;
	executingMode: 'cancel' | 'both' | null = null;
	executingStep = '';
	cancelPreview: { success: boolean; message?: string; request_payload?: unknown; channel?: string } | null = null;
	splitPreview: Tpl3plSplitBackorderPreviewResponse | null = null;
	canCancelWarehouse = true;

	constructor(
		public dialogRef: MatDialogRef<CancelAndSplitBackorderDialogComponent, { success: boolean; cancel_only?: boolean; new_orderno?: string | null } | undefined>,
		@Inject(MAT_DIALOG_DATA) public data: CancelAndSplitBackorderDialogData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {
		this.canCancelWarehouse = !!data?.canCancelWarehouse;
	}

	ngOnInit(): void {
		const on = (this.data.orderno || '').trim();
		if (!on) {
			this.loading = false;
			this.splitPreview = { success: false, message: 'Order number missing' };
			return;
		}
		forkJoin({
			cancel: this.tpl3plService.cancelOrderPreview(on).pipe(
				catchError(() => of({ success: false, message: 'Cancel preview request failed' } as { success: boolean; message?: string }))
			),
			split: this.tpl3plService.splitBackorderPreview(on).pipe(
				catchError(() => of({ success: false, message: 'Split preview request failed' } as Tpl3plSplitBackorderPreviewResponse))
			),
		}).subscribe({
			next: ({ cancel, split }) => {
				this.cancelPreview = cancel;
				this.splitPreview = split;
				this.loading = false;
			},
			error: () => {
				this.cancelPreview = { success: false, message: 'Failed to load previews' };
				this.splitPreview = { success: false, message: 'Failed to load previews' };
				this.loading = false;
			},
		});
	}

	get splitPreviewQohTooltip(): string {
		const w = this.splitPreview?.fillability_loccode ? this.splitPreview.fillability_loccode : 'fillability warehouse';
		return 'Physical QOH (SUM locstock.quantity) at ' + w + '.';
	}

	get splitPreviewSimAvailTooltip(): string {
		return 'Simulated remaining quantity when this line is reached in the global open-order queue walk at the fillability warehouse.';
	}

	splitPreviewHasKitHeaderLines(p: Tpl3plSplitBackorderPreviewResponse | null): boolean {
		if (!p) {
			return false;
		}
		const lines = [...(p.lines_stay || []), ...(p.lines_move || [])];
		return lines.some((l) => l.fillability_kit_header === true);
	}

	splitLineSimAvailRowTooltip(l: Tpl3plSplitBackorderLine): string {
		if (l.fillability_kit_header) {
			return 'Kit header: sim. avail. matches this line’s outstanding and does not decrement the shared SKU simulation pool.';
		}
		return this.splitPreviewSimAvailTooltip;
	}

	splitLineQohDisplay(l: { qoh?: number | null }): number {
		if (l == null || l.qoh == null || (typeof l.qoh === 'number' && Number.isNaN(l.qoh))) {
			return 0;
		}
		return Math.max(0, Number(l.qoh));
	}

	splitSimAvailDisplay(l: { simulated_available?: number | null }): number {
		if (l == null || l.simulated_available == null) {
			return 0;
		}
		const n = Number(l.simulated_available);
		return Number.isNaN(n) ? 0 : n;
	}

	get canSplitExecute(): boolean {
		const p = this.splitPreview;
		if (!p || !p.success || !p.split_applicable || p.no_op) {
			return false;
		}
		return (p.lines_move?.length ?? 0) > 0;
	}

	get canRunWarehouseCancel(): boolean {
		return this.canCancelWarehouse && !!this.cancelPreview?.success;
	}

	get canRunCancelAndSplit(): boolean {
		return this.canRunWarehouseCancel && this.canSplitExecute && !this.splitPreview?.picking_blocked && !this.splitPreview?.tree_blocked;
	}

	runCancelWarehouseOnly(): void {
		if (!this.canRunWarehouseCancel || this.executing) {
			return;
		}
		const on = (this.data.orderno || '').trim();
		if (
			!window.confirm(
				'Cancel this order at the warehouse (PGS) only?\n\n' +
					'Order ' +
					on +
					'\n\nThe ERP will not be changed. The export queue row will be reset to pending after a successful cancel.'
			)
		) {
			return;
		}
		this.executing = true;
		this.executingMode = 'cancel';
		this.executingStep = 'Cancelling at warehouse…';
		this.tpl3plService.cancelOrder(on, { sync_erp_cancel: false }).subscribe({
			next: (r) => {
				this.executing = false;
				this.executingMode = null;
				this.executingStep = '';
				const ok = !!(r && r.success);
				this.globalSearchService.showNotification(ok ? (r.message || 'Cancelled at warehouse') : (r.message || 'Cancel failed'), ok ? 'success' : 'warning', 'bottom', 'right');
				if (ok) {
					this.dialogRef.close({ success: true, cancel_only: true });
				}
			},
			error: (err) => {
				this.executing = false;
				this.executingMode = null;
				this.executingStep = '';
				const m = err?.error?.message || err?.message || 'Cancel request failed';
				this.globalSearchService.showNotification(m, 'warning', 'bottom', 'right');
			},
		});
	}

	runCancelWarehouseThenSplit(): void {
		if (!this.canRunCancelAndSplit || this.executing) {
			return;
		}
		const on = (this.data.orderno || '').trim();
		const n = this.splitPreview?.lines_move?.length ?? 0;
		if (
			!window.confirm(
				'Cancel at warehouse, then split into a back-order SO?\n\n' +
					'Order ' +
					on +
					'\n\nStep 1: DELETE outbound at PGS (ERP unchanged), queue → pending.\n' +
					'Step 2: Create new SO with ' +
					n +
					' short line(s).\n\nIf step 2 fails, the warehouse is already cancelled — fix from split dialog or order entry.'
			)
		) {
			return;
		}
		this.executing = true;
		this.executingMode = 'both';
		this.executingStep = 'Step 1 of 2: Cancelling at warehouse…';
		this.tpl3plService.cancelOrder(on, { sync_erp_cancel: false }).subscribe({
			next: (c) => {
				if (!c?.success) {
					this.executing = false;
					this.executingMode = null;
					this.executingStep = '';
					this.globalSearchService.showNotification(c?.message || 'Warehouse cancel failed', 'warning', 'bottom', 'right');
					return;
				}
				this.executingStep = 'Step 2 of 2: Executing split…';
				this.tpl3plService.splitBackorderExecute(on).subscribe({
					next: (s) => {
						this.executing = false;
						this.executingMode = null;
						this.executingStep = '';
						if (s.success) {
							const msg = s.new_orderno
								? `Warehouse cancelled; back order SO ${s.new_orderno} created (${s.moved_line_count ?? 0} line(s)).`
								: (s.message || 'Cancel and split complete.');
							this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
							this.dialogRef.close({ success: true, cancel_only: false, new_orderno: s.new_orderno ?? null });
						} else {
							this.globalSearchService.showNotification(
								s.message || 'Split failed after warehouse cancel — queue is pending; retry split from the queue.',
								'warning',
								'bottom',
								'right'
							);
							this.dialogRef.close({ success: false });
						}
					},
					error: () => {
						this.executing = false;
						this.executingMode = null;
						this.executingStep = '';
						this.globalSearchService.showNotification(
							'Split failed after warehouse cancel — queue is pending; retry split from the queue.',
							'warning',
							'bottom',
							'right'
						);
						this.dialogRef.close({ success: false });
					},
				});
			},
			error: (err) => {
				this.executing = false;
				this.executingMode = null;
				this.executingStep = '';
				const m = err?.error?.message || err?.message || 'Cancel request failed';
				this.globalSearchService.showNotification(m, 'warning', 'bottom', 'right');
			},
		});
	}
}

export interface SplitUninvoicedOpenLinesDialogData {
	orderno: string;
}

@Component({
	selector: 'app-split-uninvoiced-open-lines-dialog',
	template: `
		<h2 mat-dialog-title>Split uninvoiced lines → new sales order</h2>
		<mat-dialog-content class="split-ui-dialog-content">
			<p class="small text-muted mb-2">Order <span class="text-monospace">{{ data.orderno }}</span></p>
			<p class="small text-muted mb-2">
				Moves every line that still has open quantity (<code>quantity &gt; qtyinvoiced</code>) to a new SO. Lines that are already fully invoiced stay here. For partially invoiced lines, the invoiced quantity stays on this SO and the remainder is copied to the new SO.
			</p>
			<div *ngIf="loading" class="d-flex align-items-center py-3">
				<mat-spinner diameter="32" class="mr-2"></mat-spinner>
				<span>Loading preview…</span>
			</div>
			<ng-container *ngIf="!loading && preview">
				<div class="alert alert-warning small" *ngIf="preview.picking_blocked">{{ preview.message || 'Split is blocked.' }}</div>
				<div class="alert alert-warning small" *ngIf="preview.tree_blocked || preview.has_kit_structure">{{ preview.message }}</div>
				<div class="alert alert-info small" *ngIf="preview.success && preview.split_applicable && preview.no_op">{{ preview.message }}</div>
				<div class="alert alert-danger small" *ngIf="!preview.success && !preview.picking_blocked && !preview.tree_blocked">{{ preview.message || 'Preview failed' }}</div>
				<div *ngIf="(preview.lines_move?.length || 0) > 0">
					<h4 class="h6 mt-2">Lines / actions (new SO)</h4>
					<table class="table table-sm table-bordered">
						<thead>
							<tr>
								<th>Line</th>
								<th>SKU</th>
								<th>Description</th>
								<th>Mode</th>
								<th class="text-right">Qty</th>
								<th class="text-right">Invoiced</th>
								<th class="text-right">Open</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let l of preview.lines_move">
								<td>{{ l.orderlineno }}</td>
								<td class="text-monospace">{{ l.stkcode }}</td>
								<td class="split-ui-desc">{{ l.description || '—' }}</td>
								<td>{{ l.move_mode === 'split_remainder' ? 'Split remainder' : 'Move whole line' }}</td>
								<td class="text-right">{{ l.quantity }}</td>
								<td class="text-right">{{ l.qtyinvoiced }}</td>
								<td class="text-right">{{ l.remaining }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<div *ngIf="(preview.lines_stay?.length || 0) > 0">
					<h4 class="h6 mt-3">Open lines staying on this order (no open qty left after others move)</h4>
					<table class="table table-sm table-bordered">
						<thead>
							<tr>
								<th>Line</th>
								<th>SKU</th>
								<th>Description</th>
								<th class="text-right">Qty</th>
								<th class="text-right">Invoiced</th>
								<th class="text-right">Open</th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let l of preview.lines_stay">
								<td>{{ l.orderlineno }}</td>
								<td class="text-monospace">{{ l.stkcode }}</td>
								<td class="split-ui-desc">{{ l.description || '—' }}</td>
								<td class="text-right">{{ l.quantity }}</td>
								<td class="text-right">{{ l.qtyinvoiced }}</td>
								<td class="text-right">{{ l.remaining }}</td>
							</tr>
						</tbody>
					</table>
				</div>
				<p class="small text-muted mt-2 mb-0" *ngIf="preview.split_applicable && !preview.no_op && (preview.lines_move?.length || 0) > 0">
					Freight on the new SO is set to 0. New SO <code>customerref</code> is <code>OP&lt;{{ data.orderno }}&gt;</code>. You can bump the new SO # for 3PL if needed. Header totals on both orders may need reconciliation.
				</p>
			</ng-container>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Close</button>
			<button mat-raised-button color="primary" type="button" (click)="execute()" [disabled]="!canExecute || executing">
				<mat-spinner *ngIf="executing" diameter="20" class="d-inline-block mr-1"></mat-spinner>
				{{ executing ? 'Working…' : 'Execute split' }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.split-ui-dialog-content {
				min-width: 640px;
				max-width: 900px;
				max-height: 70vh;
				overflow: auto;
			}
			.split-ui-desc {
				max-width: 240px;
				white-space: normal;
				word-break: break-word;
				vertical-align: top;
			}
		`
	]
})
export class SplitUninvoicedOpenLinesDialogComponent implements OnInit {
	loading = true;
	executing = false;
	preview: Tpl3plSplitUninvoicedOpenLinesPreviewResponse | null = null;

	constructor(
		public dialogRef: MatDialogRef<SplitUninvoicedOpenLinesDialogComponent, { success: boolean; new_orderno?: string | null } | undefined>,
		@Inject(MAT_DIALOG_DATA) public data: SplitUninvoicedOpenLinesDialogData,
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService
	) {}

	ngOnInit(): void {
		this.tpl3plService.splitUninvoicedOpenLinesPreview(this.data.orderno).subscribe({
			next: (p) => {
				this.preview = p;
				this.loading = false;
			},
			error: () => {
				this.preview = { success: false, message: 'Preview request failed' };
				this.loading = false;
			}
		});
	}

	get canExecute(): boolean {
		const p = this.preview;
		if (!p || !p.success || !p.split_applicable || p.no_op) {
			return false;
		}
		const n = p.lines_move?.length ?? 0;
		return n > 0;
	}

	execute(): void {
		if (!this.canExecute || this.executing || !this.data.orderno) {
			return;
		}
		this.executing = true;
		this.tpl3plService.splitUninvoicedOpenLinesExecute(this.data.orderno).subscribe({
			next: (r) => {
				this.executing = false;
				if (r.success) {
					const msg = r.new_orderno
						? `New SO ${r.new_orderno} created (${r.moved_line_count ?? 0} line action(s)).`
						: (r.message || 'Split complete.');
					this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
					this.dialogRef.close({ success: true, new_orderno: r.new_orderno ?? null });
				} else {
					this.globalSearchService.showNotification(r.message || 'Split failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.executing = false;
				this.globalSearchService.showNotification('Split request failed', 'danger', 'bottom', 'right');
			}
		});
	}
}

export interface RawPushResultRow {
	orderno: string;
	success: boolean;
	message: string;
	fullResult: any;
}

@Component({
	selector: 'app-tpl3pl-export-queue',
	templateUrl: './tpl3pl-export-queue.component.html',
	styleUrls: ['./tpl3pl-export-queue.component.scss']
})
export class Tpl3plExportQueueComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;

	displayedColumns: string[] = ['select', 'orderno', 'debtorno', 'customer_name', 'payterms', 'export_open_order_total', 'export_preauth_held_total', 'export_preauth', 'tpl3pl_remote_order_id', 'export_status', 'state', 'erp_status', 'erp_fillability', 'erp_line_sim', 'placedby', 'enteredby', 'created_at', 'export_attempts', 'last_export_attempt', 'error_message', 'full_resubmit', 'actions'];
	dataSource = new MatTableDataSource<ExportQueueItem>();
	/** Checked queue rows for batch export, remove, warehouse cancel, split, etc. */
	readonly selection = new SelectionModel<ExportQueueItem>(true, [], true, (a, b) => a.id === b.id);
	private allItems: ExportQueueItem[] = [];
	loading = false;
	processing = false;
	batchDeleting = false;
	exportingReport = false;
	/** Sequential mark-dispatched for checked failed/dead-letter rows. */
	batchMarkingDispatched = false;
	/** Sequential split-backorder preview → execute for checked eligible rows. */
	batchSplitting = false;
	batchSplitProgress = '';
	/** Combined split-then-export pass for checked rows (split + send all). */
	batchSplitExporting = false;
	batchSplitExportProgress = '';
	batchCancellingWarehouse = false;
	batchWarehouseCancelProgress = '';
	batchPreparingSplitResubmit = false;
	batchPrepareSplitResubmitProgress = '';
	/** Sequential bump SO # (PGS cancel when applicable) for checked rows. */
	batchBumpingOrdernoForResubmit = false;
	batchBumpOrdernoProgress = '';
	exportingOrderNo: string | null = null;
	/** Row currently sending warehouse-only cancel (sync_erp_cancel: false). */
	cancellingWarehouseOnlyOrderNo: string | null = null;
	/** Server: export_status IN (...); empty = all statuses. */
	exportStatusFilters: string[] = [];
	/** Server: state IN (...); empty = all. */
	queueStateFilters: string[] = [];
	/** Exact order number (server). */
	orderFilter = '';
	/** Server: orderno LIKE %…% */
	orderContainsFilter = '';
	pgsIdContains = '';
	/** Server: customer name, debtor #, branch, or customer ref (LIKE). */
	customerContainsFilter = '';
	errorContains = '';
	/** Paste CSV or one SO # per line — server returns only those rows on the queue (AND with other filters). */
	csvOrderListFilterRaw = '';
	erpStatusFilters: number[] = [];
	/** salesorders.salesperson (salesmancode); server filter. */
	salespersonCodes: string[] = [];
	/** Active reps from globals/salesman (current = 1). */
	salespeopleForSelect: { code: string; label: string }[] = [];
	/** Client-side on the loaded page of rows (fillability is computed after fetch). */
	fillabilityFilters: string[] = [];
	orderStatuses: any[] = [];
	includeArchived = false;
	/**
	 * When set, export/retry skips payment export gates: prepay/CC terms coverage and PreAuth full open-order hold
	 * (server treats both as operator override — e.g. after an order split voided the card hold).
	 */
	overrideCcPaymentGate = false;
	overrideAvailabilityGate = false;
	/** Collapsed by default; the rarely-used filters live behind the "More filters" toggle. */
	showAdvancedFilters = false;
	lastExportResult: any = null;
	previewLoadingOrderNo: string | null = null;
	selectedErrorItem: ExportQueueItem | null = null;
	selectedErrorDetails: {
		message: string;
		errorCode?: string;
		canRetry: boolean;
		response?: any;
		circuitOpen?: boolean;
		/** From PGS export failure: SKUs reported missing at warehouse (may be one per response). */
		skusNotFound?: string[];
		/** SKUs we sent on the outbound JSON (full list for comparison). */
		outboundSkus?: string[];
	} | null = null;
	rawPushInProgress = false;
	rawPushProgress = '';
	rawPushLimit = 10;
	rawPushResults: RawPushResultRow[] = [];
	rawPushResultsDataSource = new MatTableDataSource<RawPushResultRow>([]);
	rawPushResultsColumns: string[] = ['orderno', 'status', 'message', 'view_raw'];

	massStatusUpdating = false;
	/** Progress text while sequential PGS batches run (e.g. "Processed 75 order(s)…"). */
	massStatusProgress = '';
	/** Max orders per HTTP request; client chains requests until PGS queue is exhausted. */
	pgsMassBatchSize = 25;
	massStatusResult: { checked: number; updated: number; unchanged: number; failed: number; erp_status_updated?: number; details: any[]; batch_count?: number } | null = null;

	/** Row context for the shared per-row actions mat-menu (set on trigger click). */
	exportQueueRowMenuItem: ExportQueueItem | null = null;

	/** Source SO # while bump → split → export pathway runs (spinner on that queue row). */
	fullPathwayBusyOrderno: string | null = null;

	/** From 3PL config: enables split → BO actions on partially fillable queue rows. */
	splitPartialBackorderEnabled = false;
	/** From 3PL config: allows bump SO # for resubmit (independently of split). */
	bumpResubmitEnabled = false;
	/** From 3PL config: move uninvoiced open qty to a new SO (invoiced lines stay). */
	splitUninvoicedOpenLinesEnabled = false;
	private tpl3plConfigSavedSub: Subscription | null = null;

	readonly exportStatusOptions: { value: string; label: string }[] = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'exporting', label: 'Exporting' },
		{ value: 'exported', label: 'Exported' },
		{ value: 'failed', label: 'Failed' },
		{ value: 'dead_letter', label: 'Dead letter' },
	];

	readonly queueStateOptions: { value: string; label: string }[] = [
		{ value: 'Created', label: 'Created' },
		{ value: 'Validated', label: 'Validated' },
		{ value: 'Queued', label: 'Queued' },
		{ value: 'Exporting', label: 'Exporting' },
		{ value: 'Submitted', label: 'Submitted' },
		{ value: 'Dispatched', label: 'Dispatched' },
		{ value: 'Shipped', label: 'Shipped' },
		{ value: 'Invoiced', label: 'Invoiced' },
		{ value: 'Failed', label: 'Failed' },
		{ value: 'DeadLetter', label: 'Dead letter' },
		{ value: 'Cancelled', label: 'Cancelled' },
		{ value: 'BackOrder', label: 'Back order' },
	];

	readonly fillabilityOptions: { value: string; label: string }[] = [
		{ value: 'fillable', label: 'Fillable' },
		{ value: 'partial', label: 'Partially fillable' },
		{ value: 'not_fillable', label: 'Not fillable' },
		{ value: 'unknown', label: 'Unknown' },
	];

	get eligibleOrdersCount(): number {
		const data = this.dataSource.data || [];
		return data.filter((r: ExportQueueItem) => this.isRowBatchEligible(r)).length;
	}

	isRowBatchEligible(item: ExportQueueItem): boolean {
		if (
			item.export_status !== 'pending' &&
			item.export_status !== 'failed' &&
			item.export_status !== 'dead_letter'
		) {
			return false;
		}
		if (item.export_needs_preauth && !this.overrideCcPaymentGate) {
			return false;
		}
		if (item.export_needs_cc_payment && !this.overrideCcPaymentGate) {
			return false;
		}
		return true;
	}

	/** Blocks per-row Export / Retry when pre-auth or CC/prepay gate applies (unless override payment gate is on). */
	isRowExportActionBlocked(item: ExportQueueItem): boolean {
		if (item.export_needs_preauth && !this.overrideCcPaymentGate) {
			return true;
		}
		return !!(item.export_needs_cc_payment && !this.overrideCcPaymentGate);
	}

	exportActionBlockedTooltip(item: ExportQueueItem): string {
		if (item.export_needs_preauth && !this.overrideCcPaymentGate) {
			return item.export_preauth_message || 'Re-authorize on the order, or enable Override payment gate to export without a matching hold.';
		}
		if (item.export_needs_cc_payment && !this.overrideCcPaymentGate) {
			return item.export_cc_payment_message || 'Card authorization must cover open balance for these payment terms, or enable override';
		}
		return '';
	}

	/** Same rule as the per-row Remove action (batch remove uses this). */
	canRemoveFromQueue(item: ExportQueueItem): boolean {
		const s = item.export_status;
		return s === 'failed' || s === 'pending' || s === 'dead_letter';
	}

	/** Failed or dead-letter: already at 3PL (e.g. duplicate export) — mark exported / Dispatched without re-calling the API. */
	canMarkDispatchedFromFailure(item: ExportQueueItem): boolean {
		const s = item.export_status;
		return s === 'failed' || s === 'dead_letter';
	}

	/**
	 * Checkbox: pending / failed / dead letter (queue ops), exported rows eligible for
	 * warehouse-only cancel or split / resubmit workflows, and prepare-split-resubmit targets.
	 */
	isRowCheckboxSelectable(item: ExportQueueItem): boolean {
		return (
			this.canRemoveFromQueue(item) ||
			this.canCancelWarehouseOnly(item) ||
			this.canPrepareSplitResubmit(item) ||
			this.canOpenSplitBackorderDialog(item) ||
			this.canBumpOrdernoFor3plResubmit(item)
		);
	}

	getCheckboxSelectableLoadedRows(): ExportQueueItem[] {
		return (this.dataSource.data || []).filter((r) => this.isRowCheckboxSelectable(r));
	}

	/** Rows on the current page that can be checked for batch actions (not only removable queue rows). */
	get removableOrdersCount(): number {
		return this.getCheckboxSelectableLoadedRows().length;
	}

	getEligibleLoadedRows(): ExportQueueItem[] {
		return (this.dataSource.data || []).filter((r) => this.isRowBatchEligible(r));
	}

	get selectedBatchEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.isRowBatchEligible(r));
	}

	get selectedBatchEligibleCount(): number {
		return this.selectedBatchEligible.length;
	}

	isAllCheckboxRowsSelected(): boolean {
		const rows = this.getCheckboxSelectableLoadedRows();
		return rows.length > 0 && rows.every((r) => this.selection.isSelected(r));
	}

	isCheckboxRowsSelectionIndeterminate(): boolean {
		const rows = this.getCheckboxSelectableLoadedRows();
		const n = rows.filter((r) => this.selection.isSelected(r)).length;
		return n > 0 && n < rows.length;
	}

	onMasterCheckboxChange(checked: boolean): void {
		const rows = this.getCheckboxSelectableLoadedRows();
		if (rows.length === 0) {
			return;
		}
		if (checked) {
			rows.forEach((r) => this.selection.select(r));
		} else {
			rows.forEach((r) => this.selection.deselect(r));
		}
	}

	setRowCheckboxSelected(item: ExportQueueItem, checked: boolean): void {
		if (!this.isRowCheckboxSelectable(item)) {
			return;
		}
		if (checked) {
			this.selection.select(item);
		} else {
			this.selection.deselect(item);
		}
	}

	get selectedRemovableFromQueue(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canRemoveFromQueue(r));
	}

	get selectedRemovableCount(): number {
		return this.selectedRemovableFromQueue.length;
	}

	get selectedMarkDispatchedEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canMarkDispatchedFromFailure(r));
	}

	get selectedMarkDispatchedCount(): number {
		return this.selectedMarkDispatchedEligible.length;
	}

	get selectedBatchSplitEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canOpenSplitBackorderDialog(r));
	}

	get selectedBatchSplitCount(): number {
		return this.selectedBatchSplitEligible.length;
	}

	get selectedWarehouseCancelEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canCancelWarehouseOnly(r));
	}

	get selectedWarehouseCancelCount(): number {
		return this.selectedWarehouseCancelEligible.length;
	}

	get selectedPrepareSplitResubmitEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canPrepareSplitResubmit(r));
	}

	get selectedPrepareSplitResubmitCount(): number {
		return this.selectedPrepareSplitResubmitEligible.length;
	}

	get selectedBumpOrdernoEligible(): ExportQueueItem[] {
		return this.selection.selected.filter((r) => this.canBumpOrdernoFor3plResubmit(r));
	}

	get selectedBumpOrdernoCount(): number {
		return this.selectedBumpOrdernoEligible.length;
	}

	clearBatchSelection(): void {
		this.selection.clear();
	}

	get rowExportActionsLocked(): boolean {
		return (
			this.processing ||
			this.loading ||
			!!this.exportingOrderNo ||
			this.batchDeleting ||
			this.batchMarkingDispatched ||
			this.batchSplitting ||
			this.batchSplitExporting ||
			this.batchCancellingWarehouse ||
			this.batchPreparingSplitResubmit ||
			this.batchBumpingOrdernoForResubmit ||
			!!this.fullPathwayBusyOrderno
		);
	}

	constructor(
		private tpl3plService: Tpl3plService,
		private globalSearchService: GlobalSearchService,
		private router: Router,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private ordersService: OrdersService,
		private globalsService: GlobalsService,
		private printService: PrintService
	) {
		this.bindQueueTableSortAccessor();
	}

	/** MatTableDataSource client-side sort keys (paginator shows one page; sort applies to loaded rows). */
	private bindQueueTableSortAccessor(): void {
		const exportStatusSortRank: Record<string, number> = {
			pending: 0,
			exporting: 1,
			failed: 2,
			dead_letter: 3,
			exported: 4,
		};
		const fillabilitySortRank: Record<string, number> = {
			fillable: 0,
			partial: 1,
			not_fillable: 2,
			unknown: 3,
		};
		this.dataSource.sortingDataAccessor = (row: ExportQueueItem, columnId: string) => {
			switch (columnId) {
				case 'orderno': {
					const s = String(row.orderno || '').trim();
					if (/^\d+$/.test(s)) {
						return s.padStart(24, '0');
					}
					return s.toLowerCase();
				}
				case 'debtorno': {
					const d = String(row.debtorno || '').trim();
					const b = String(row.branchcode || '').trim();
					return d ? `${d.toLowerCase()}/${b.toLowerCase()}` : '';
				}
				case 'customer_name':
					return (row.customer_name || '').toLowerCase();
				case 'payterms':
					return (row.payterms || '').toLowerCase();
				case 'export_open_order_total':
					return row.export_open_order_total != null && row.export_open_order_total !== undefined
						? Number(row.export_open_order_total)
						: -1;
				case 'export_preauth_held_total':
					return row.export_preauth_held_total != null && row.export_preauth_held_total !== undefined
						? Number(row.export_preauth_held_total)
						: -1;
				case 'export_preauth':
					return (row.export_needs_preauth ? 2 : 0) + (row.export_needs_cc_payment ? 1 : 0);
				case 'tpl3pl_remote_order_id':
					return String(row.tpl3pl_remote_order_id || '').toLowerCase();
				case 'export_status':
					return exportStatusSortRank[row.export_status] ?? 99;
				case 'state':
					return (row.state || '').toLowerCase();
				case 'erp_status': {
					const n = Number(row.erp_order_status_id);
					return Number.isFinite(n) ? n : -1;
				}
				case 'erp_fillability': {
					const f = String(row.erp_fillability || '').toLowerCase();
					return fillabilitySortRank[f] ?? 5;
				}
				case 'erp_line_sim': {
					const open = Number(row.erp_fill_lines_open) || 0;
					const cov = Number(row.erp_fill_lines_covered) || 0;
					if (open <= 0) {
						return -1;
					}
					return cov / open;
				}
				case 'placedby':
					return (row.placedby || '').toLowerCase();
				case 'enteredby':
					return (row.enteredby || '').toLowerCase();
				case 'created_at':
					return row.created_at ? new Date(row.created_at).getTime() : 0;
				case 'export_attempts':
					return Number(row.export_attempts) || 0;
				case 'last_export_attempt':
					return row.last_export_attempt ? new Date(row.last_export_attempt).getTime() : 0;
				case 'error_message':
					return (this.getErrorSummary(row) || '').toLowerCase();
				default:
					return (row as any)[columnId];
			}
		};
	}

	get orderStatusesForSelect(): any[] {
		return [...this.orderStatuses]
			.filter((s) => s && !isNaN(Number(s.order_status_id)))
			.sort((a, b) => Number(a.order_status_id) - Number(b.order_status_id));
	}

	ngOnInit(): void {
		this.ordersService.getOrderStatuses().subscribe({
			next: (r: any) => {
				this.orderStatuses = Array.isArray(r) ? r : (r && Array.isArray(r.data) ? r.data : []);
			},
			error: () => {
				this.orderStatuses = [];
			}
		});
		this.globalsService.getSalesman().subscribe({
			next: (rows: any) => {
				const arr = Array.isArray(rows) ? rows : [];
				const byCode = new Map<string, { code: string; label: string }>();
				for (const r of arr) {
					const code = String(r.salesmancode ?? '').trim();
					if (!code || byCode.has(code)) {
						continue;
					}
					const name = String(r.salesmanname ?? '').trim();
					const label = name ? `${name} (${code})` : code;
					byCode.set(code, { code, label });
				}
				this.salespeopleForSelect = [...byCode.values()].sort((a, b) =>
					a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
				);
			},
			error: () => {
				this.salespeopleForSelect = [];
			}
		});
		this.refreshTpl3plSplitFlag();
		this.tpl3plConfigSavedSub = this.tpl3plService.configSaved$.subscribe(() => this.refreshTpl3plSplitFlag());
		this.loadQueue();
	}

	private refreshTpl3plSplitFlag(): void {
		this.tpl3plService.getConfig().subscribe({
			next: (c) => {
				const cj = c?.config_json || {};
				this.splitPartialBackorderEnabled = !!cj.split_partial_backorder_enabled;
				this.bumpResubmitEnabled = !!cj.split_partial_backorder_enabled || !!cj.bump_so_resubmit_enabled;
				this.splitUninvoicedOpenLinesEnabled = !!cj.split_uninvoiced_open_lines_enabled;
			},
			error: () => {
				this.splitPartialBackorderEnabled = false;
				this.bumpResubmitEnabled = false;
				this.splitUninvoicedOpenLinesEnabled = false;
			}
		});
	}

	/** Open split preview: partial fillability, or order already split / BO child of a split (read-only tables still useful). */
	canOpenSplitBackorderDialog(item: ExportQueueItem): boolean {
		if (!this.splitPartialBackorderEnabled || !item?.orderno) {
			return false;
		}
		const hasSplitHistory = !!(item.tpl3pl_split_executed || item.tpl3pl_split_parent_orderno);
		const partial = item.erp_fillability === 'partial';
		const openish =
			item.export_status === 'pending' ||
			item.export_status === 'failed' ||
			item.export_status === 'dead_letter' ||
			item.export_status === 'exporting' ||
			item.export_status === 'exported';
		const st = (item.state || '').trim();
		/** Match tpl3pl OrderStateMachine + stuck rows where export_status is failed/dead_letter but erp_fillability is not "partial". */
		const postPgsRelease =
			st === 'BackOrder' ||
			st === 'Cancelled' ||
			st === 'DeadLetter' ||
			st === 'Failed' ||
			(!!item.archived_at && item.export_status === 'exported');
		const er = item.export_response;
		const preparedForResplit =
			er &&
			typeof er === 'object' &&
			((er as { tpl3pl_prepare_split_resubmit?: boolean }).tpl3pl_prepare_split_resubmit === true ||
				(er as { tpl3pl_renumbered_for_3pl_resubmit?: boolean }).tpl3pl_renumbered_for_3pl_resubmit === true);
		return openish && (partial || hasSplitHistory || postPgsRelease || preparedForResplit);
	}

	/** After cancel at PGS or queue back-order: reset row to pending and clear idempotency / PGS anchor. */
	canPrepareSplitResubmit(item: ExportQueueItem): boolean {
		if (!item?.orderno) {
			return false;
		}
		const st = (item.state || '').trim();
		if (st === 'BackOrder') {
			return item.export_status === 'failed' || item.export_status === 'exported';
		}
		if (st === 'Cancelled') {
			return item.export_status === 'exported';
		}
		return !!item.archived_at && item.export_status === 'exported';
	}

	/** PGS cancel + allocate new SO # (details, payment, queue, lots, OMS, …). */
	canBumpOrdernoFor3plResubmit(item: ExportQueueItem): boolean {
		if (!this.bumpResubmitEnabled || !item?.orderno) {
			return false;
		}
		if (item.export_status === 'exporting') {
			return false;
		}
		return (
			item.export_status === 'exported' ||
			item.export_status === 'failed' ||
			item.export_status === 'pending' ||
			item.export_status === 'dead_letter'
		);
	}

	/** Bump → split (if preview moves lines) → export; ERP status from server on successful export. */
	canRunFullResubmitPathway(item: ExportQueueItem): boolean {
		return this.canBumpOrdernoFor3plResubmit(item);
	}

	isFullResubmitPathwayLocked(item: ExportQueueItem): boolean {
		if (
			this.processing ||
			this.loading ||
			this.batchDeleting ||
			this.batchSplitting ||
			this.batchSplitExporting ||
			this.batchMarkingDispatched ||
			this.batchCancellingWarehouse ||
			this.batchPreparingSplitResubmit ||
			this.batchBumpingOrdernoForResubmit
		) {
			return true;
		}
		if (this.fullPathwayBusyOrderno && this.fullPathwayBusyOrderno !== item.orderno) {
			return true;
		}
		if (this.exportingOrderNo && this.exportingOrderNo !== item.orderno) {
			return true;
		}
		return false;
	}

	startFullResubmitPathway(orderno: string): void {
		const on = (orderno || '').trim();
		if (!on) {
			return;
		}
		this.runFullResubmitPathwayPipeline(on, true);
	}

	private firstMeaningfulMessage(...values: unknown[]): string {
		for (const value of values) {
			if (typeof value === 'string') {
				const trimmed = value.trim();
				if (trimmed !== '') {
					return trimmed;
				}
				continue;
			}
			if (!value || typeof value !== 'object') {
				continue;
			}
			const obj = value as Record<string, unknown>;
			const nested = this.firstMeaningfulMessage(
				obj.message,
				obj.error,
				obj.error_message,
				obj.statusText,
				obj.pgs_cancel,
				obj.pgs_result,
				obj.response
			);
			if (nested) {
				return nested;
			}
		}
		return '';
	}

	private getBumpFailureMessage(bumpRes: unknown): string {
		const msg = this.firstMeaningfulMessage(bumpRes);
		return msg || 'Renumber request failed';
	}

	private bumpOrdernoConfirmMessage(count: number, sampleOrderno?: string): string {
		const head =
			count === 1
				? 'Bump order ' + String(sampleOrderno || '').trim() + ' to the next SO # and re-queue?'
				: 'Bump ' + count + ' checked orders to the next SO # and re-queue?';
		return (
			head +
			'\n\nRenumbers the order (including invoiced lines) so the 3PL will accept it again, then resets the queue row to pending for export. Existing invoices in debtor trans keep the original SO # — that history isn’t rewritten.'
		);
	}

	private confirmBumpIgnorePgsCancelFailure(): boolean {
		return window.confirm(
			'If the warehouse already removed the order, PGS cancel may fail.\n\n' +
				'OK = treat not-found style cancel errors as OK. Cancel = require a successful PGS cancel.'
		);
	}

	private getRequestFailureMessage(err: unknown, fallback: string): string {
		const msg = this.firstMeaningfulMessage(err);
		return msg || fallback;
	}

	private runFullResubmitPathwayPipeline(startOrderno: string, ignorePgsCancelFailure: boolean): void {
		this.fullPathwayBusyOrderno = startOrderno;
		this.tpl3plService
			.bumpOrdernoFor3plResubmit(startOrderno, { ignore_pgs_cancel_failure: ignorePgsCancelFailure })
			.pipe(
				concatMap((bumpRes) => {
					if (!bumpRes.success || !bumpRes.new_orderno) {
						return throwError(() => new Error(this.getBumpFailureMessage(bumpRes)));
					}
					const newOn = String(bumpRes.new_orderno);
					return this.tpl3plService.splitBackorderPreview(newOn).pipe(
						concatMap((preview) => of({ newOn, preview }))
					);
				}),
				concatMap(({ newOn, preview }) => {
					if (preview.picking_blocked || preview.tree_blocked) {
						return throwError(() => new Error(preview.message || 'Split preview blocked (picking or BOM tree).'));
					}
					if (!preview.success) {
						return throwError(() => new Error(preview.message || 'Split preview failed'));
					}
					const linesMove = preview.lines_move?.length ?? 0;
					const needSplit = preview.split_applicable === true && !preview.no_op && linesMove > 0;
					if (needSplit) {
						return this.tpl3plService.splitBackorderExecute(newOn).pipe(
							concatMap((splitRes) => {
								if (!splitRes.success) {
									return throwError(() => new Error(splitRes.message || 'Split failed'));
								}
								return of(newOn);
							})
						);
					}
					return of(newOn);
				}),
				concatMap((newOn) =>
					this.tpl3plService
						.exportOrder(newOn, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate, true)
						.pipe(concatMap((exp) => of({ newOn, exp })))
				),
				finalize(() => {
					this.fullPathwayBusyOrderno = null;
				})
			)
			.subscribe({
				next: ({ newOn, exp }) => {
					this.loadQueue(true);
					if (exp?.success) {
						this.globalSearchService.showNotification(
							'Full resubmit finished: exported SO ' +
								newOn +
								'. ERP status was updated on successful export if configured in 3PL settings.',
							'success',
							'bottom',
							'right'
						);
					} else {
						this.globalSearchService.showNotification(
							exp?.message || 'Export failed after bump/split for SO ' + newOn,
							'warning',
							'bottom',
							'right'
						);
					}
				},
				error: (err: unknown) => {
					this.loadQueue(true);
					const msg = err instanceof Error
						? err.message
						: this.getRequestFailureMessage(err, 'Full resubmit failed');
					this.globalSearchService.showNotification(msg, 'warning', 'bottom', 'right');
				},
			});
	}


	bumpOrdernoFor3plResubmit(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const msg = this.bumpOrdernoConfirmMessage(1, item.orderno);
		if (!window.confirm(msg)) {
			return;
		}
		this.tpl3plService.bumpOrdernoFor3plResubmit(item.orderno, { ignore_pgs_cancel_failure: true }).subscribe({
			next: (r) => {
				this.loadQueue(true);
				const detail = r.success && r.new_orderno ? ' New SO #' + r.new_orderno + '.' : '';
				this.globalSearchService.showNotification(
					this.getBumpFailureMessage(r) + detail,
					r.success ? 'success' : 'warning',
					'bottom',
					'right'
				);
			},
			error: (err: unknown) => {
				this.globalSearchService.showNotification(
					this.getRequestFailureMessage(err, 'Renumber request failed'),
					'warning',
					'bottom',
					'right'
				);
			},
		});
	}

	openSplitBackorderDialog(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const orderno = String(item.orderno);
		this.dialog
			.open(SplitBackorderDialogComponent, {
				width: '820px',
				maxWidth: '95vw',
				data: {
					orderno,
					showFullResubmitPathway: this.canRunFullResubmitPathway(item),
					onFullResubmitPathway: (on: string) => this.startFullResubmitPathway(on),
				} as SplitBackorderDialogData,
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.success) {
					this.loadQueue(true);
				}
			});
	}

	/** Preview PGS warehouse cancel + partial split (both previews in one dialog). */
	canOpenCancelAndSplitBackorderPreviewDialog(item: ExportQueueItem): boolean {
		if (!item?.orderno) {
			return false;
		}
		return this.canCancelWarehouseOnly(item) || this.canOpenSplitBackorderDialog(item);
	}

	openCancelAndSplitBackorderDialog(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const orderno = String(item.orderno);
		this.dialog
			.open(CancelAndSplitBackorderDialogComponent, {
				width: '900px',
				maxWidth: '96vw',
				maxHeight: '92vh',
				data: {
					orderno,
					canCancelWarehouse: this.canCancelWarehouseOnly(item),
				} as CancelAndSplitBackorderDialogData,
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.success) {
					this.loadQueue(true);
				}
			});
	}

	/** Uninvoiced open qty → new SO (invoiced lines stay). Not tied to fillability “partial”. */
	canOpenSplitUninvoicedOpenLinesDialog(item: ExportQueueItem): boolean {
		if (!this.splitUninvoicedOpenLinesEnabled || !item?.orderno) {
			return false;
		}
		const openish =
			item.export_status === 'pending' ||
			item.export_status === 'failed' ||
			item.export_status === 'dead_letter' ||
			item.export_status === 'exporting' ||
			item.export_status === 'exported';
		return openish;
	}

	openSplitUninvoicedOpenLinesDialog(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const orderno = String(item.orderno);
		this.dialog
			.open(SplitUninvoicedOpenLinesDialogComponent, {
				width: '780px',
				maxWidth: '95vw',
				data: { orderno } as SplitUninvoicedOpenLinesDialogData,
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.success) {
					this.loadQueue(true);
				}
			});
	}

	ngAfterViewInit(): void {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
	}

	queueTotal: number | null = null;
	queueStats: Tpl3plQueueStatsCounts | null = null;
	statsLoading = false;
	/** True when fillability filter hit the server scan cap (more matches may exist). */
	fillabilityScanCapped = false;
	private serverFilterReloadTimer: ReturnType<typeof setTimeout> | null = null;
	/** Serializes queue loads; cancels in-flight HTTP when filters change (prevents stale unfiltered rows overwriting the table). */
	private readonly queueReload$ = new Subject<{ preservePage: boolean }>();
	private queuePipelineSub: Subscription | null = null;
	private queueStatsSubscription: Subscription | null = null;

	/** Snapshot of UI filter state for tpl3pl/orders/queue POST body. */
	private buildExportQueueFilterPayload(): Record<string, unknown> {
		const csvOrderList = parseWarehouseCancelOrderListInput((this.csvOrderListFilterRaw || '').trim());
		let pageLimit = this.fillabilityFilters.length > 0 ? 500 : 100;
		if (csvOrderList.length > 0) {
			pageLimit = Math.min(500, Math.max(pageLimit, csvOrderList.length));
		}
		const filters: Record<string, unknown> = { limit: pageLimit, offset: 0, includeArchived: this.includeArchived };
		if (csvOrderList.length > 0) {
			filters.orderno_in = csvOrderList.slice(0, 500).join(',');
		}
		if (this.exportStatusFilters.length > 0) {
			filters.statuses = [...this.exportStatusFilters];
		}
		if (this.queueStateFilters.length > 0) {
			filters.states = [...this.queueStateFilters];
		}
		const orderSearch = (this.orderFilter || '').trim();
		if (orderSearch) {
			filters.orderno = orderSearch;
		}
		const orderContains = (this.orderContainsFilter || '').trim();
		if (orderContains) {
			filters.orderno_contains = orderContains;
		}
		const pgsPart = (this.pgsIdContains || '').trim();
		if (pgsPart) {
			filters.pgs_order_id_contains = pgsPart;
		}
		const errPart = (this.errorContains || '').trim();
		if (errPart) {
			filters.error_contains = errPart;
		}
		const custPart = (this.customerContainsFilter || '').trim();
		if (custPart) {
			filters.customer_contains = custPart;
		}
		if (this.erpStatusFilters.length > 0) {
			filters.erp_status_ids = [...this.erpStatusFilters];
		}
		if (this.salespersonCodes.length > 0) {
			filters.salesperson = [...this.salespersonCodes];
		}
		if (this.fillabilityFilters.length > 0) {
			filters.fillabilities = [...this.fillabilityFilters];
		}
		return filters;
	}

	private wireExportQueueReloadPipeline(): void {
		if (this.queuePipelineSub && !this.queuePipelineSub.closed) {
			return;
		}
		this.queuePipelineSub = this.queueReload$
			.pipe(
				switchMap(({ preservePage }) => {
					this.refreshQueueStats();
					this.loading = true;
					this.selection.clear();
					this.selectedErrorItem = null;
					this.selectedErrorDetails = null;
					if (!preservePage && this.dataSource.paginator) {
						this.dataSource.paginator.firstPage();
					}
					const filters = this.buildExportQueueFilterPayload();
					const csvOrderList = parseWarehouseCancelOrderListInput((this.csvOrderListFilterRaw || '').trim());
					return this.tpl3plService.getExportQueue(filters as any).pipe(
						map((result) => ({ result, preservePage, listed: csvOrderList.length })),
						catchError(() => {
							this.globalSearchService.showNotification('Error loading export queue', 'danger', 'bottom', 'right');
							return EMPTY;
						}),
						finalize(() => {
							this.loading = false;
						})
					);
				})
			)
			.subscribe({
				next: ({ result, preservePage, listed }) => {
					this.allItems = result.data || [];
					this.queueTotal = result.total ?? null;
					this.fillabilityScanCapped = !!result.fillability_scan_capped;
					this.applyFilters(!preservePage);
					if (listed > 0 && this.allItems.length < listed) {
						this.globalSearchService.showNotification(
							'Order # list: ' +
								listed +
								' listed · ' +
								this.allItems.length +
								' row(s) on the queue match (others are not on the queue or excluded by filters).',
							'warning',
							'bottom',
							'right'
						);
					}
				}
			});
	}

	loadQueue(preservePage = false): void {
		this.wireExportQueueReloadPipeline();
		this.queueReload$.next({ preservePage });
	}

	private refreshQueueStats(): void {
		if (this.queueStatsSubscription) {
			this.queueStatsSubscription.unsubscribe();
			this.queueStatsSubscription = null;
		}
		const firstLoad = this.queueStats === null;
		if (firstLoad) {
			this.statsLoading = true;
		}
		this.queueStatsSubscription = this.tpl3plService
			.getQueueStats()
			.pipe(
				finalize(() => {
					this.queueStatsSubscription = null;
				})
			)
			.subscribe({
				next: (r) => {
					this.statsLoading = false;
					if (r.success && r.counts) {
						const c = r.counts;
						this.queueStats = {
							...c,
							exported_not_shipped: c.exported_not_shipped ?? 0,
							open_queue_fillability: c.open_queue_fillability ?? {
								fillable: 0,
								partial: 0,
								not_fillable: 0,
								unknown: 0,
								open_queue_orders: 0
							}
						};
					}
				},
				error: () => {
					this.statsLoading = false;
				}
			});
	}

	applyQueueFilters(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.loadQueue();
	}

	/** Count of active filters/options living in the collapsed "More filters" section (drives the toggle badge). */
	get advancedFilterCount(): number {
		let n = 0;
		if (this.queueStateFilters.length > 0) { n++; }
		if (this.erpStatusFilters.length > 0) { n++; }
		if (this.salespersonCodes.length > 0) { n++; }
		if ((this.orderContainsFilter || '').trim()) { n++; }
		if ((this.pgsIdContains || '').trim()) { n++; }
		if ((this.errorContains || '').trim()) { n++; }
		if ((this.csvOrderListFilterRaw || '').trim()) { n++; }
		if (this.overrideCcPaymentGate) { n++; }
		if (this.overrideAvailabilityGate) { n++; }
		return n;
	}

	/** Fillability is applied on the server (scans a larger window than the page size). */
	isPillActive(arr: any[], val: any): boolean {
		return arr.includes(val);
	}

	togglePill(arr: any[], val: any, callback: () => void): void {
		const idx = arr.indexOf(val);
		if (idx === -1) {
			arr.push(val);
		} else {
			arr.splice(idx, 1);
		}
		callback();
	}

	/** Debounced reload when export status / queue state / fillability / ERP status multi-select changes. */
	onServerQueueFiltersChange(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
		}
		this.serverFilterReloadTimer = setTimeout(() => {
			this.serverFilterReloadTimer = null;
			this.lastExportResult = null;
			this.loadQueue();
		}, 400);
	}

	/** Not exported (pending, failed, exporting, dead letter) + fillable — one click. */
	applyNotExportedFillablePreset(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.exportStatusFilters = ['pending', 'failed', 'exporting', 'dead_letter'];
		this.fillabilityFilters = ['fillable'];
		this.loadQueue();
	}

	ngOnDestroy(): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
		}
		if (this.queuePipelineSub) {
			this.queuePipelineSub.unsubscribe();
			this.queuePipelineSub = null;
		}
		this.queueReload$.complete();
		if (this.queueStatsSubscription) {
			this.queueStatsSubscription.unsubscribe();
			this.queueStatsSubscription = null;
		}
		if (this.tpl3plConfigSavedSub) {
			this.tpl3plConfigSavedSub.unsubscribe();
			this.tpl3plConfigSavedSub = null;
		}
	}

	refreshQueue(): void {
		this.lastExportResult = null;
		this.loadQueue();
	}

	openFailedExportMissingSkusDialog(): void {
		this.dialog.open(FailedExportMissingSkusDialogComponent, {
			width: '920px',
			maxWidth: '96vw',
			maxHeight: '92vh',
			data: {
				tpl3plService: this.tpl3plService,
				includeArchived: this.includeArchived,
				days: 90,
				limit: 2000
			}
		});
	}

	/** Quick filter from stat cards: show only rows in these export statuses (reloads queue). */
	setExportStatusFilterOnly(statuses: string[]): void {
		if (this.serverFilterReloadTimer) {
			clearTimeout(this.serverFilterReloadTimer);
			this.serverFilterReloadTimer = null;
		}
		this.lastExportResult = null;
		this.exportStatusFilters = statuses.length ? [...statuses] : [];
		this.loadQueue();
	}

	clearFiltersAndReload(): void {
		this.lastExportResult = null;
		this.exportStatusFilters = [];
		this.queueStateFilters = [];
		this.orderFilter = '';
		this.orderContainsFilter = '';
		this.pgsIdContains = '';
		this.customerContainsFilter = '';
		this.errorContains = '';
		this.erpStatusFilters = [];
		this.salespersonCodes = [];
		this.fillabilityFilters = [];
		this.fillabilityScanCapped = false;
		this.includeArchived = false;
		this.csvOrderListFilterRaw = '';
		this.loadQueue();
	}

dismissLastResult(): void {
	this.lastExportResult = null;
}

	private queueExportEscape(value: unknown): string {
		return String(value ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	private queueExportValue(value: unknown, fallback = '—'): string {
		const s = String(value ?? '').trim();
		return s !== '' ? s : fallback;
	}

	private queueExportMoney(value: number | null | undefined): string {
		if (value === null || value === undefined) {
			return '—';
		}
		const n = Number(value);
		if (!Number.isFinite(n)) {
			return '—';
		}
		return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
	}

	private queueExportFilterSummary(): string {
		const parts: string[] = [];
		if (this.exportStatusFilters.length > 0) {
			parts.push('Export status: ' + this.exportStatusFilters.join(', '));
		}
		if (this.queueStateFilters.length > 0) {
			parts.push('Queue state: ' + this.queueStateFilters.join(', '));
		}
		if (this.fillabilityFilters.length > 0) {
			parts.push('Fillability: ' + this.fillabilityFilters.join(', '));
		}
		if (this.erpStatusFilters.length > 0) {
			parts.push('ERP status: ' + this.erpStatusFilters.join(', '));
		}
		if (this.salespersonCodes.length > 0) {
			parts.push('Salesperson: ' + this.salespersonCodes.join(', '));
		}
		if ((this.orderFilter || '').trim() !== '') {
			parts.push('Order #: ' + this.orderFilter.trim());
		}
		if ((this.orderContainsFilter || '').trim() !== '') {
			parts.push('Order contains: ' + this.orderContainsFilter.trim());
		}
		if ((this.customerContainsFilter || '').trim() !== '') {
			parts.push('Customer: ' + this.customerContainsFilter.trim());
		}
		if ((this.pgsIdContains || '').trim() !== '') {
			parts.push('3PL order ID: ' + this.pgsIdContains.trim());
		}
		if ((this.errorContains || '').trim() !== '') {
			parts.push('Error: ' + this.errorContains.trim());
		}
		if (this.includeArchived) {
			parts.push('Including archived');
		}
		const csvN = this.csvOrderListFilterParsedCount;
		if (csvN > 0) {
			parts.push('SO list filter: ' + csvN + ' #');
		}
		return parts.join(' | ');
	}

	get csvOrderListFilterParsedCount(): number {
		return parseWarehouseCancelOrderListInput((this.csvOrderListFilterRaw || '').trim()).length;
	}

	onCsvOrderListFilterFileSelected(ev: Event): void {
		const input = ev.target as HTMLInputElement;
		const file = input.files && input.files[0];
		if (!file) {
			return;
		}
		const reader = new FileReader();
		reader.onload = () => {
			this.csvOrderListFilterRaw = typeof reader.result === 'string' ? reader.result : '';
			input.value = '';
		};
		reader.readAsText(file);
	}

	buildQueueHtmlAndExport(doctype: 'pdf' | 'xlsx'): void {
		const rows = this.dataSource.data || [];
		if (!rows.length || this.exportingReport) {
			return;
		}
		this.exportingReport = true;
		let html = '<table style="width: 100%; border-collapse: collapse;"><thead><tr style="background: black; color: white;">';
		html += '<th style="text-align:left">Order</th>';
		html += '<th style="text-align:left">Debtor</th>';
		html += '<th style="text-align:left">Customer</th>';
		html += '<th style="text-align:left">Terms</th>';
		html += '<th style="text-align:right">Open total</th>';
		html += '<th style="text-align:right">Auth held</th>';
		html += '<th style="text-align:left">Pre-auth</th>';
		html += '<th style="text-align:left">3PL Order ID</th>';
		html += '<th style="text-align:left">Export Status</th>';
		html += '<th style="text-align:left">PGS Order Status</th>';
		html += '<th style="text-align:left">Queue State</th>';
		html += '<th style="text-align:left">ERP Status</th>';
		html += '<th style="text-align:left">Fillability</th>';
		html += '<th style="text-align:center">Lines</th>';
		html += '<th style="text-align:left">Salesperson</th>';
		html += '<th style="text-align:left">Entered By</th>';
		html += '<th style="text-align:left">Recorded</th>';
		html += '<th style="text-align:center">Attempts</th>';
		html += '<th style="text-align:left">Last Attempt</th>';
		html += '<th style="text-align:left">Last Message</th>';
		html += '</tr></thead><tbody>';
		rows.forEach((item) => {
			const debtor = item.debtorno ? `${item.debtorno}${item.branchcode ? '/' + item.branchcode : ''}` : '—';
			const erpStatus = item.erp_order_status_name || item.erp_order_status_id || '—';
			const fillability = item.erp_fillability_label || item.erp_fillability || '—';
			const lines = (item.erp_fill_lines_open || 0) > 0
				? `${item.erp_fill_lines_covered || 0}/${item.erp_fill_lines_open || 0}`
				: '—';
			const recorded = item.created_at ? new Date(item.created_at).toLocaleString() : '—';
			const lastAttempt = item.last_export_attempt ? new Date(item.last_export_attempt).toLocaleString() : '—';
			html += '<tr>';
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.orderno))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(debtor)}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.customer_name))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.payterms))}</td>`;
			const openTot = this.queueExportMoney(item.export_open_order_total);
			const authHeld = this.queueExportMoney(item.export_preauth_held_total);
			const preauthCol = item.export_needs_preauth
				? 'Re-auth required'
				: item.export_needs_cc_payment
					? 'CC / prepay required'
					: 'OK';
			html += `<td style="text-align:right">${this.queueExportEscape(openTot)}</td>`;
			html += `<td style="text-align:right">${this.queueExportEscape(authHeld)}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(preauthCol)}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.tpl3pl_remote_order_id))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(this.getStatusLabel(item.export_status)))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(this.getPgsStatusLabel(item)))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.state))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(erpStatus))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(fillability))}</td>`;
			html += `<td style="text-align:center">${this.queueExportEscape(lines)}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.placedby))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(item.enteredby))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(recorded)}</td>`;
			html += `<td style="text-align:center">${this.queueExportEscape(String(item.export_attempts ?? 0))}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(lastAttempt)}</td>`;
			html += `<td style="text-align:left">${this.queueExportEscape(this.queueExportValue(this.getErrorSummary(item), ''))}</td>`;
			html += '</tr>';
		});
		html += '</tbody></table>';
		if (doctype === 'pdf') {
			this.exportQueuePdf(html);
			return;
		}
		this.exportQueueXls(html);
	}

	exportQueuePdf(html: string): void {
		const now = new Date();
		const createdAt = now.toLocaleString();
		const filterStr = this.queueExportFilterSummary();
		const encoded = this.globalSearchService.base64encode(html);
		const data = {
			content: encoded,
			filename: `3PL Export Queue - ${createdAt}`,
			title: '3PL Export Queue',
			subtitle: (filterStr ? filterStr + ' | ' : '') + 'Rows: ' + (this.dataSource.data?.length || 0) + ' | Created ' + createdAt
		};
		this.printService.pdf(data).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadPdf(result.content, data.filename);
				this.exportingReport = false;
			},
			error: () => {
				this.exportingReport = false;
				this.globalSearchService.showNotification('PDF export failed', 'warning', 'bottom', 'right');
			}
		});
	}

	exportQueueXls(html: string): void {
		const now = new Date();
		const createdAt = now.toLocaleString();
		const filterStr = this.queueExportFilterSummary();
		const encoded = this.globalSearchService.base64encode(html);
		const data = {
			content: encoded,
			filename: `3PL Export Queue - ${createdAt}`,
			title: '3PL Export Queue',
			subtitle: (filterStr ? filterStr + ' | ' : '') + 'Rows: ' + (this.dataSource.data?.length || 0) + ' | Created ' + createdAt
		};
		this.printService.xls(data).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadXls(result.content, data.filename);
				this.exportingReport = false;
			},
			error: () => {
				this.exportingReport = false;
				this.globalSearchService.showNotification('XLS export failed', 'warning', 'bottom', 'right');
			}
		});
	}

	private applyFilters(resetPage = true): void {
		const rows = this.allItems.slice();

		const prevIndex = resetPage ? 0 : (this.dataSource.paginator?.pageIndex ?? 0);
		const prevSize = this.dataSource.paginator?.pageSize;
		this.dataSource.data = rows;
		if (this.dataSource.paginator) {
			if (resetPage) {
				this.dataSource.paginator.firstPage();
			} else if (prevSize != null) {
				this.dataSource.paginator.pageSize = prevSize;
				const pageCount = Math.max(1, Math.ceil(rows.length / prevSize));
				this.dataSource.paginator.pageIndex = Math.min(prevIndex, pageCount - 1);
			}
		}
	}

	processQueue(): void {
		this.processing = true;
		this.lastExportResult = null;
		this.tpl3plService.processQueue().subscribe({
			next: (result) => {
				this.loadQueue(true);
				if (result.success) {
					this.lastExportResult = result;
					this.globalSearchService.showNotification('Processed ' + (result.processed || 0) + ' orders', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('Batch had failures – see order rows', 'warning', 'bottom', 'right');
				}
				this.processing = false;
			},
			error: (error) => {
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Error processing queue', 'warning', 'bottom', 'right');
				this.processing = false;
			}
		});
	}

	retryExport(item: ExportQueueItem): void {
		this.lastExportResult = null;
		this.exportingOrderNo = item.orderno;
		this.tpl3plService.retryExport(item.id, item.orderno, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	prepareSplitResubmit(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		this.tpl3plService.prepareSplitResubmit(item.orderno).subscribe({
			next: (r) => {
				this.loadQueue(true);
				this.globalSearchService.showNotification(
					r.message || (r.success ? 'Queue ready' : 'Not updated'),
					r.success ? 'success' : 'warning',
					'bottom',
					'right'
				);
			},
			error: () => {
				this.globalSearchService.showNotification('Request failed', 'warning', 'bottom', 'right');
			}
		});
	}

	batchPrepareSplitResubmitSelected(): void {
		const items = this.selectedPrepareSplitResubmitEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification(
				'No selected rows are eligible for prepare split resubmit',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		const msg =
			'Reset ' +
			items.length +
			' queue row(s) for split resubmit (clear idempotency / PGS anchor per row)?\n\nERP orders are not cancelled by this action.';
		if (!window.confirm(msg)) {
			return;
		}
		this.batchPreparingSplitResubmit = true;
		let index = 0;
		let ok = 0;
		let failed = 0;
		const total = items.length;
		const run = (): void => {
			if (index >= items.length) {
				this.batchPreparingSplitResubmit = false;
				this.batchPrepareSplitResubmitProgress = '';
				this.loadQueue(true);
				this.globalSearchService.showNotification(
					'Prepare split resubmit: ' + ok + ' ok' + (failed > 0 ? ', ' + failed + ' failed' : ''),
					failed > 0 ? 'warning' : 'success',
					'bottom',
					'right'
				);
				return;
			}
			const item = items[index];
			index++;
			this.batchPrepareSplitResubmitProgress = index + ' / ' + total;
			this.tpl3plService.prepareSplitResubmit(item.orderno).subscribe({
				next: (r) => {
					if (r?.success) {
						ok++;
					} else {
						failed++;
					}
					run();
				},
				error: () => {
					failed++;
					run();
				}
			});
		};
		run();
	}

	/**
	 * Show warehouse / 3PL cancel actions for exported rows.
	 * Do not hide when queue state is Cancelled — ERP may be cancelled first while warehouse cancel is still needed.
	 * Only hide when fulfillment is effectively done at the warehouse, unless the row is archived (ERP already
	 * considered the order done, but a warehouse cancel is still needed — e.g. user cancelled in ERP after archiving).
	 */
	canCancelWarehouseOnly(item: ExportQueueItem): boolean {
		if (!item) {
			return false;
		}
		if (item.export_status !== 'exported') {
			return false;
		}
		const st = (item.state || '').trim();
		// Already cancelled at the warehouse — nothing left to do.
		if (st === 'Cancelled') {
			return false;
		}
		// Archived rows: allow cancel even when PGS state is Shipped/Invoiced.
		// The order is considered done on the ERP side, but the user may still need
		// to cancel the outbound at the warehouse (e.g. they cancelled in the ERP after archiving).
		if (item.archived_at) {
			return true;
		}
		return st !== 'Shipped' && st !== 'Invoiced';
	}

	/** One-step cancel at 3PL when the ERP order is already cancelled — does not change ERP lines/status/picking. */
	cancelAtWarehouseOnly(item: ExportQueueItem): void {
		if (!item?.orderno || !this.canCancelWarehouseOnly(item)) {
			return;
		}
		const st = (item.state || '').trim();
		const shippedWarning =
			item.archived_at && (st === 'Shipped' || st === 'Invoiced')
				? '\n\n⚠️ PGS shows this order as ' + st + '. Only proceed if you have confirmed with the warehouse that the outbound has NOT physically left.'
				: '';
		const msg =
			'Cancel this order at the warehouse (3PL) only?\n\n' +
			'Order ' +
			item.orderno +
			'\n\nThe ERP will not be changed. Use when the sales order is already cancelled in the ERP but the outbound is still open at the warehouse.' +
			shippedWarning;
		if (!window.confirm(msg)) {
			return;
		}
		this.cancellingWarehouseOnlyOrderNo = item.orderno;
		this.tpl3plService.cancelOrder(item.orderno, { sync_erp_cancel: false }).subscribe({
			next: (result) => {
				this.cancellingWarehouseOnlyOrderNo = null;
				this.loadQueue(true);
				const ok = !!(result && result.success);
				this.globalSearchService.showNotification(
					ok ? (result.message || 'Cancelled at warehouse') : 'Cancel failed: ' + (result.message || ''),
					ok ? 'success' : 'warning',
					'bottom',
					'right'
				);
			},
			error: (err) => {
				this.cancellingWarehouseOnlyOrderNo = null;
				const m = err?.error?.message || err?.message || 'Cancel request failed';
				this.globalSearchService.showNotification(m, 'warning', 'bottom', 'right');
			}
		});
	}

	batchCancelWarehouseSelected(): void {
		const items = this.selectedWarehouseCancelEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification(
				'Select exported rows (not shipped/invoiced at 3PL) to cancel at the warehouse',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		const msg =
			'Cancel ' +
			items.length +
			' order(s) at the warehouse (3PL) only?\n\nThe ERP will not be changed. Use when sales orders are already handled in the ERP but the outbound is still open at the warehouse.';
		if (!window.confirm(msg)) {
			return;
		}
		this.runWarehouseCancelForOrdernos(items.map((i) => i.orderno));
	}

	/**
	 * Sequential 3PL-only cancels (ERP unchanged). Caller must confirm if needed.
	 */
	runWarehouseCancelForOrdernos(ordernos: string[]): void {
		const list = Array.isArray(ordernos)
			? ordernos.map((o) => String(o || '').trim()).filter((o) => o !== '')
			: [];
		const unique: string[] = [];
		const seen = new Set<string>();
		for (const o of list) {
			if (!seen.has(o)) {
				seen.add(o);
				unique.push(o);
			}
		}
		if (unique.length === 0) {
			return;
		}
		this.batchCancellingWarehouse = true;
		let index = 0;
		let ok = 0;
		let failed = 0;
		const total = unique.length;
		const run = (): void => {
			if (index >= unique.length) {
				this.batchCancellingWarehouse = false;
				this.batchWarehouseCancelProgress = '';
				this.loadQueue(true);
				this.globalSearchService.showNotification(
					'Warehouse cancel: ' + ok + ' ok' + (failed > 0 ? ', ' + failed + ' failed' : ''),
					failed > 0 ? 'warning' : 'success',
					'bottom',
					'right'
				);
				return;
			}
			const orderno = unique[index];
			index++;
			this.batchWarehouseCancelProgress = index + ' / ' + total;
			this.tpl3plService.cancelOrder(orderno, { sync_erp_cancel: false }).subscribe({
				next: (result) => {
					if (result && result.success) {
						ok++;
					} else {
						failed++;
					}
					run();
				},
				error: () => {
					failed++;
					run();
				}
			});
		};
		run();
	}

	openCsvWarehouseCancelDialog(): void {
		if (this.rowExportActionsLocked) {
			return;
		}
		this.dialog
			.open(CsvWarehouseCancelDialogComponent, {
				width: '640px',
				maxWidth: '96vw',
				maxHeight: '90vh',
				autoFocus: true,
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.ordernos?.length) {
					this.runWarehouseCancelForOrdernos(r.ordernos);
				}
			});
	}

	batchBumpOrdernoSelected(): void {
		const items = this.selectedBumpOrdernoEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification(
				'No selected rows are eligible to bump SO # (exporting rows can’t be bumped)',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		if (!window.confirm(this.bumpOrdernoConfirmMessage(items.length))) {
			return;
		}
		const queue: ExportQueueItem[] = [...items];
		this.batchBumpingOrdernoForResubmit = true;
		let index = 0;
		let ok = 0;
		const failures: { orderno: string; reason: string }[] = [];
		const total = queue.length;
		const ignoreOpts = { ignore_pgs_cancel_failure: true };
		const run = (): void => {
			if (index >= queue.length) {
				this.batchBumpingOrdernoForResubmit = false;
				this.batchBumpOrdernoProgress = '';
				this.loadQueue(true);
				let msg = 'Bumped ' + ok + ' / ' + total + ' — queued for export.';
				if (failures.length) {
					const sample = failures.slice(0, 3).map((f) => f.orderno + ': ' + f.reason).join(' | ');
					const more = failures.length > 3 ? ' (+' + (failures.length - 3) + ' more)' : '';
					msg = 'Bumped ' + ok + ' / ' + total + '. Failed ' + failures.length + ' — ' + sample + more;
				}
				this.globalSearchService.showNotification(
					msg,
					failures.length > 0 ? 'warning' : 'success',
					'bottom',
					'right'
				);
				return;
			}
			const item = queue[index];
			const on = String(item.orderno || '').trim();
			index++;
			this.batchBumpOrdernoProgress = index + ' / ' + total;
			if (!on) {
				failures.push({ orderno: '(blank)', reason: 'missing orderno' });
				run();
				return;
			}
			this.tpl3plService.bumpOrdernoFor3plResubmit(on, ignoreOpts).subscribe({
				next: (r) => {
					if (r && r.success) {
						ok++;
					} else {
						failures.push({ orderno: on, reason: (r?.error_code || 'failed') + (r?.message ? ': ' + r.message : '') });
					}
					run();
				},
				error: (err: unknown) => {
					failures.push({ orderno: on, reason: this.getRequestFailureMessage(err, 'request failed') });
					run();
				}
			});
		};
		run();
	}

	cancelOrder(item: ExportQueueItem): void {
		if (!item?.orderno) { return; }
		this.tpl3plService.cancelOrderPreview(item.orderno).subscribe({
			next: (preview) => {
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'cancel',
						orderno: item.orderno,
						export_status: item.export_status,
						content: preview.success && preview.request_payload ? { request_payload: preview.request_payload } : {},
						onSent: () => this.loadQueue(true)
					},
					panelClass: 'raw-response-dialog'
				});
			},
			error: () => {
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'cancel',
						orderno: item.orderno,
						export_status: item.export_status,
						content: {},
						onSent: () => this.loadQueue(true)
					},
					panelClass: 'raw-response-dialog'
				});
			}
		});
	}

	forceResendExported(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const msg =
			'Re-send order ' +
			item.orderno +
			' to the 3PL?\n\nUse when the warehouse removed it from their system. This bypasses “already shipped” and duplicate-send protection for this attempt only.';
		if (!window.confirm(msg)) {
			return;
		}
		this.lastExportResult = null;
		this.exportingOrderNo = item.orderno;
		this.tpl3plService.retryExport(item.id, item.orderno, true, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order re-sent to 3PL', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification(result.message || 'Re-send failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Re-send request failed', 'warning', 'bottom', 'right');
			}
		});
	}

	forceResendByOrderField(): void {
		const orderno = (this.orderFilter || '').trim();
		if (!orderno) {
			this.globalSearchService.showNotification('Enter an order number first', 'warning', 'bottom', 'right');
			return;
		}
		const msg =
			'Force re-send order ' +
			orderno +
			' to the 3PL?\n\nUse when the warehouse removed it from their system. This bypasses “already shipped” and duplicate-send protection for this attempt only.';
		if (!window.confirm(msg)) {
			return;
		}
		this.lastExportResult = null;
		this.exportingOrderNo = orderno;
		this.tpl3plService.retryExport(undefined, orderno, true, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order re-sent to 3PL', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification(result.message || 'Re-send failed', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('Re-send request failed', 'warning', 'bottom', 'right');
			}
		});
	}

	exportOrder(orderno: string): void {
		this.lastExportResult = null;
		this.exportingOrderNo = orderno;
		this.tpl3plService.exportOrder(orderno, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate).subscribe({
			next: (result) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				if (result.success) {
					this.globalSearchService.showNotification('Order exported', 'success', 'bottom', 'right');
				} else {
					this.lastExportResult = null;
					this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.exportingOrderNo = null;
				this.loadQueue(true);
				this.lastExportResult = null;
				this.globalSearchService.showNotification('See error on order row', 'warning', 'bottom', 'right');
			}
		});
	}

	getEligibleOrdersForRawPush(): ExportQueueItem[] {
		const data = this.dataSource.data || [];
		return data
			.filter(
				(r: ExportQueueItem) =>
					r.export_status === 'pending' ||
					r.export_status === 'failed' ||
					r.export_status === 'dead_letter'
			)
			.slice(0, this.rawPushLimit);
	}

	/** Sequential export/retry for a specific list (checkbox batch or first-N raw push). */
	private runSequentialExportBatch(eligible: ExportQueueItem[]): void {
		if (eligible.length === 0) {
			return;
		}
		this.rawPushInProgress = true;
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
		const total = eligible.length;
		let index = 0;
		const run = () => {
			if (index >= eligible.length) {
				this.rawPushProgress = '';
				this.rawPushInProgress = false;
				const succeeded = this.rawPushResults.filter(r => r.success).length;
				const failed = this.rawPushResults.length - succeeded;
				if (failed === 0) {
					this.dismissRawPushResults();
				} else {
					this.rawPushResultsDataSource.data = [...this.rawPushResults];
				}
				this.loadQueue(true);
				this.globalSearchService.showNotification('Export batch finished: ' + succeeded + ' ok, ' + failed + ' failed', failed > 0 ? 'warning' : 'success', 'bottom', 'right');
				return;
			}
			const item = eligible[index];
			this.rawPushProgress = (index + 1) + ' of ' + total;
			const useRetry =
				item.id &&
				(item.export_status === 'failed' || item.export_status === 'dead_letter');
			const obs = useRetry
				? this.tpl3plService.retryExport(item.id, item.orderno, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate)
				: this.tpl3plService.exportOrder(item.orderno, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate);
			obs.subscribe({
				next: (result: any) => {
					const normalized = result && typeof result === 'object' ? result : { success: false, message: 'No response' };
					this.rawPushResults.push({
						orderno: item.orderno,
						success: !!normalized.success,
						message: normalized.message || (normalized.success ? 'Exported' : 'Failed'),
						fullResult: normalized
					});
					index++;
					run();
				},
				error: (err) => {
					this.rawPushResults.push({
						orderno: item.orderno,
						success: false,
						message: err?.error?.message || err?.message || 'Request failed',
						fullResult: { success: false, message: err?.error?.message || err?.message }
					});
					index++;
					run();
				}
			});
		};
		run();
	}

	exportSelectedBatch(): void {
		const eligible = this.selectedBatchEligible;
		if (eligible.length === 0) {
			this.globalSearchService.showNotification('Select pending or failed orders to export', 'warning', 'bottom', 'right');
			return;
		}
		this.runSequentialExportBatch(eligible);
	}

	batchSplitSelected(): void {
		const items = this.selectedBatchSplitEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification(
				'No selected rows are eligible for split (enable partial back-order split in 3PL config; row must be partial / split-related / post-PGS release)',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		this.batchSplitting = true;
		let index = 0;
		let splitOk = 0;
		let splitSkipped = 0;
		let splitFailed = 0;
		const total = items.length;
		const run = (): void => {
			if (index >= items.length) {
				this.batchSplitting = false;
				this.batchSplitProgress = '';
				this.loadQueue(true);
				const parts: string[] = [];
				if (splitOk > 0) {
					parts.push(splitOk + ' split');
				}
				if (splitSkipped > 0) {
					parts.push(splitSkipped + ' skipped (nothing to move)');
				}
				if (splitFailed > 0) {
					parts.push(splitFailed + ' failed');
				}
				const msg = parts.length > 0 ? parts.join(', ') : 'No changes';
				this.globalSearchService.showNotification(msg, splitFailed > 0 ? 'warning' : 'success', 'bottom', 'right');
				return;
			}
			const item = items[index];
			index++;
			this.batchSplitProgress = index + ' / ' + total;
			this.tpl3plService.splitBackorderPreview(item.orderno).subscribe({
				next: (preview) => {
					if (preview.picking_blocked || preview.tree_blocked) {
						splitFailed++;
						run();
						return;
					}
					if (!preview.success) {
						splitFailed++;
						run();
						return;
					}
					const linesMove = preview.lines_move?.length ?? 0;
					const needSplit =
						preview.split_applicable === true && !preview.no_op && linesMove > 0;
					if (!needSplit) {
						splitSkipped++;
						run();
						return;
					}
					this.tpl3plService.splitBackorderExecute(item.orderno).subscribe({
						next: (execRes) => {
							if (execRes.success) {
								splitOk++;
							} else {
								splitFailed++;
							}
							run();
						},
						error: () => {
							splitFailed++;
							run();
						},
					});
				},
				error: () => {
					splitFailed++;
					run();
				},
			});
		};
		run();
	}

	/**
	 * Per selected row: preview + execute split (if applicable), then export the source SO.
	 * Each row is fully processed before moving on so state stays serialized. Not-fillable rows
	 * where split can't produce a fillable remainder are skipped for export.
	 */
	batchSplitSelectedAndExport(): void {
		const items = this.selectedBatchSplitEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification(
				'No selected rows are eligible for split + export',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		this.batchSplitExporting = true;
		let index = 0;
		let splitOk = 0;
		let splitSkipped = 0;
		let splitFailed = 0;
		let exportOk = 0;
		let exportFailed = 0;
		let exportSkipped = 0;
		const total = items.length;

		const finish = (): void => {
			this.batchSplitExporting = false;
			this.batchSplitExportProgress = '';
			this.loadQueue(true);
			const parts: string[] = [];
			parts.push(splitOk + ' split');
			if (splitSkipped > 0) {
				parts.push(splitSkipped + ' skip-split');
			}
			if (splitFailed > 0) {
				parts.push(splitFailed + ' split-fail');
			}
			parts.push(exportOk + ' exported');
			if (exportSkipped > 0) {
				parts.push(exportSkipped + ' skip-export');
			}
			if (exportFailed > 0) {
				parts.push(exportFailed + ' export-fail');
			}
			const hasFailures = splitFailed > 0 || exportFailed > 0;
			this.globalSearchService.showNotification(
				parts.join(', '),
				hasFailures ? 'warning' : 'success',
				'bottom',
				'right'
			);
		};

		const exportSource = (orderno: string, sourceItem: ExportQueueItem, done: () => void): void => {
			if (sourceItem.erp_fillability === 'not_fillable') {
				exportSkipped++;
				done();
				return;
			}
			if (this.isRowExportActionBlocked(sourceItem)) {
				exportSkipped++;
				done();
				return;
			}
			this.tpl3plService.exportOrder(orderno, false, this.overrideCcPaymentGate, undefined, this.overrideAvailabilityGate).subscribe({
				next: (result: any) => {
					if (result && result.success) {
						exportOk++;
					} else {
						exportFailed++;
					}
					done();
				},
				error: () => {
					exportFailed++;
					done();
				},
			});
		};

		const run = (): void => {
			if (index >= items.length) {
				finish();
				return;
			}
			const item = items[index];
			index++;
			this.batchSplitExportProgress = index + ' / ' + total;
			this.tpl3plService.splitBackorderPreview(item.orderno).subscribe({
				next: (preview) => {
					if (preview.picking_blocked || preview.tree_blocked || !preview.success) {
						splitFailed++;
						run();
						return;
					}
					const linesMove = preview.lines_move?.length ?? 0;
					const needSplit = preview.split_applicable === true && !preview.no_op && linesMove > 0;
					if (!needSplit) {
						splitSkipped++;
						exportSource(item.orderno, item, run);
						return;
					}
					this.tpl3plService.splitBackorderExecute(item.orderno).subscribe({
						next: (execRes) => {
							if (!execRes.success) {
								splitFailed++;
								run();
								return;
							}
							splitOk++;
							exportSource(item.orderno, item, run);
						},
						error: () => {
							splitFailed++;
							run();
						},
					});
				},
				error: () => {
					splitFailed++;
					run();
				},
			});
		};
		run();
	}

	markSelectedAsDispatched(): void {
		const items = this.selectedMarkDispatchedEligible;
		if (items.length === 0) {
			this.globalSearchService.showNotification('Select failed or dead-letter rows to mark dispatched', 'warning', 'bottom', 'right');
			return;
		}
		const msg =
			'Mark ' +
			items.length +
			' row(s) as exported / dispatched?\n\nUse when the order already exists at the 3PL (e.g. duplicate export). This does not call the 3PL API.';
		if (!window.confirm(msg)) {
			return;
		}
		this.batchMarkingDispatched = true;
		let index = 0;
		let ok = 0;
		let failed = 0;
		const run = (): void => {
			if (index >= items.length) {
				this.batchMarkingDispatched = false;
				this.applyFilters();
				this.refreshQueueStats();
				this.globalSearchService.showNotification(
					'Marked dispatched: ' + ok + (failed > 0 ? ' (' + failed + ' failed)' : ''),
					failed > 0 ? 'warning' : 'success',
					'bottom',
					'right'
				);
				return;
			}
			const item = items[index];
			index++;
			this.tpl3plService.markQueueDispatched(item.id).subscribe({
				next: (result) => {
					if (result.success) {
						ok++;
						this.patchQueueItemAsDispatched(item.id);
						this.selection.deselect(item);
						if (this.selectedErrorItem?.id === item.id) {
							this.clearErrorDetails();
						}
					} else {
						failed++;
					}
					run();
				},
				error: () => {
					failed++;
					run();
				}
			});
		};
		run();
	}

	/** Update local grid row after markDispatched API (avoids full reload per row in batch). */
	private patchQueueItemAsDispatched(id: number): void {
		const i = this.allItems.findIndex((x) => x.id === id);
		if (i < 0) {
			return;
		}
		const row = this.allItems[i];
		this.allItems[i] = {
			...row,
			export_status: 'exported',
			state: 'Dispatched',
			error_message: '',
			archived_at: null
		};
	}

	markQueueDispatchedFromFailure(item: ExportQueueItem): void {
		if (!this.canMarkDispatchedFromFailure(item)) {
			return;
		}
		const msg =
			'Mark order ' +
			item.orderno +
			' as exported / dispatched?\n\nUse when it already exists at the 3PL (e.g. duplicate export). This does not call the 3PL API.';
		if (!window.confirm(msg)) {
			return;
		}
		this.tpl3plService.markQueueDispatched(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification(result.message || 'Marked dispatched', 'success', 'bottom', 'right');
					this.patchQueueItemAsDispatched(item.id);
					this.applyFilters();
					this.refreshQueueStats();
					if (this.selectedErrorItem?.id === item.id) {
						this.clearErrorDetails();
					}
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not update queue row', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.globalSearchService.showNotification(err?.error?.message || 'Request failed', 'danger', 'bottom', 'right');
			}
		});
	}

	removeSelectedFromQueue(): void {
		const items = this.selectedRemovableFromQueue;
		if (items.length === 0) {
			this.globalSearchService.showNotification('Select pending, failed, or dead-letter rows to remove', 'warning', 'bottom', 'right');
			return;
		}
		const msg =
			'Remove ' +
			items.length +
			' row(s) from the export queue?\n\nOrders are not cancelled in the ERP; this only drops the 3PL queue row.';
		if (!window.confirm(msg)) {
			return;
		}
		this.batchDeleting = true;
		let index = 0;
		let removed = 0;
		let failed = 0;
		const run = (): void => {
			if (index >= items.length) {
				this.batchDeleting = false;
				this.applyFilters();
				this.refreshQueueStats();
				const tone = failed > 0 ? 'warning' : 'success';
				this.globalSearchService.showNotification(
					'Removed ' + removed + ' from queue' + (failed > 0 ? ' (' + failed + ' failed)' : ''),
					tone,
					'bottom',
					'right'
				);
				return;
			}
			const item = items[index];
			index++;
			this.tpl3plService.deleteQueueItem(item.id).subscribe({
				next: (result) => {
					if (result.success) {
						removed++;
						this.allItems = this.allItems.filter((i) => i.id !== item.id);
						this.selection.deselect(item);
						if (this.selectedErrorItem?.id === item.id) {
							this.clearErrorDetails();
						}
					} else {
						failed++;
					}
					run();
				},
				error: () => {
					failed++;
					run();
				}
			});
		};
		run();
	}

	rawManualPush(): void {
		const eligible = this.getEligibleOrdersForRawPush();
		if (eligible.length === 0) {
			this.globalSearchService.showNotification('No pending or failed orders to push', 'warning', 'bottom', 'right');
			return;
		}
		this.runSequentialExportBatch(eligible);
	}

	dismissRawPushResults(): void {
		this.rawPushResults = [];
		this.rawPushResultsDataSource.data = [];
	}

	openRawResponseForResult(row: RawPushResultRow): void {
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				orderno: row.orderno,
				export_status: row.success ? 'exported' : 'failed',
				content: row.fullResult,
				onSent: () => this.loadQueue(true),
				show_pre_export_reauth: true,
				override_cc_payment_gate: this.overrideCcPaymentGate,
				override_availability_gate: this.overrideAvailabilityGate
			},
			panelClass: 'raw-response-dialog'
		});
	}

	onRawPushLimitChange(): void {}

	getErpStatusClass(statusId: string | null | undefined): string {
		const n = parseInt(statusId || '', 10);
		if (isNaN(n)) { return 'erp-status-default'; }
		if (n === 80) { return 'erp-status-credit-hold'; }
		if (n === 53) { return 'erp-status-preauth-authorized'; }
		if (n === 52) { return 'erp-status-preauth-decline'; }
		if (n >= 90) { return 'erp-status-cancelled'; }
		if (n > 29 && n < 51) { return 'erp-status-invoiced'; }
		if (n <= 29) { return 'erp-status-open'; }
		return 'erp-status-default';
	}

	getFillabilityClass(fillability: string | null | undefined): string {
		switch (fillability) {
		case 'fillable': return 'erp-fill-fillable';
		case 'partial': return 'erp-fill-partial';
		case 'not_fillable': return 'erp-fill-not-fillable';
		default: return '';
		}
	}

	getPgsStatusLabel(item: ExportQueueItem): string | null {
		if (!item.pgs_status_raw) {
			return null;
		}
		try {
			const parsed = JSON.parse(item.pgs_status_raw) as Record<string, unknown> | null;
			if (!parsed || typeof parsed !== 'object') {
				return null;
			}
			if (parsed.success === false) {
				const msg = parsed.message;
				return typeof msg === 'string' && msg.trim() !== '' ? msg : null;
			}
			let block: unknown = parsed.data ?? parsed;
			if (block && typeof block === 'object' && !Array.isArray(block)) {
				const o = block as Record<string, unknown>;
				const nested = o.Data ?? o.data;
				if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
					block = nested;
				}
			}
			if (!block || typeof block !== 'object' || Array.isArray(block)) {
				return null;
			}
			const b = block as Record<string, unknown>;
			const status =
				b.Status ??
				b.status ??
				b.OrderStatus ??
				b.orderStatus ??
				b.State ??
				b.state ??
				b.orderStatusDescription ??
				null;
			if (status == null) {
				return null;
			}
			const s = String(status).trim();
			return s !== '' ? s : null;
		} catch {
			return null;
		}
	}

	getStateBadgeClass(state: string): string {
		switch ((state || '').toLowerCase()) {
		case 'picking': return 'state-badge state-badge--picking';
		case 'dispatched': return 'state-badge state-badge--dispatched';
		case 'shipped': return 'state-badge state-badge--shipped';
		case 'invoiced': return 'state-badge state-badge--invoiced';
		case 'queued':
		case 'created':
		case 'validated': return 'state-badge state-badge--queued';
		case 'failed':
		case 'deadletter': return 'state-badge state-badge--failed';
		case 'cancelled': return 'state-badge state-badge--cancelled';
		case 'backorder': return 'state-badge state-badge--backorder';
		default: return 'state-badge state-badge--default';
		}
	}

	getStatusBadgeClass(status: string): string {
		switch (status) {
		case 'exported': return 'badge-success';
		case 'failed': return 'badge-danger';
		case 'exporting': return 'badge-warning';
		case 'dead_letter': return 'badge-secondary';
		case 'cancelled': return 'badge-warning';
		default: return 'badge-secondary';
		}
	}

	getStatusLabel(status: string): string {
		switch (status) {
		case 'pending': return 'Pending';
		case 'exporting': return 'Exporting';
		case 'exported': return 'Exported';
		case 'failed': return 'Failed';
		case 'dead_letter': return 'Dead Letter';
		case 'cancelled': return 'Cancelled';
		default: return status || '—';
		}
	}

	private static readonly ERROR_SUMMARY_MAX = 72;

	getErrorSummary(item: ExportQueueItem): string {
		if (item.export_response?.circuit_open) {
			return 'Circuit breaker open';
		}
		const ex = item.export_response as {
			skus_not_found?: string[];
			outbound_skus?: string[];
		} | null;
		if (ex && Array.isArray(ex.skus_not_found) && ex.skus_not_found.length > 0) {
			const ob = Array.isArray(ex.outbound_skus) ? ex.outbound_skus.length : 0;
			const suffix = ob > 0 ? ` (${ob} SKU(s) on outbound)` : '';
			if (ex.skus_not_found.length === 1) {
				return `SKU not at 3PL: ${ex.skus_not_found[0]}${suffix} — click for details`;
			}
			return `${ex.skus_not_found.length} SKU(s) not at 3PL${suffix} — click for details`;
		}
		const msg = (item.error_message || '').trim();
		if (!msg) {
			return '';
		}
		if (msg.length <= Tpl3plExportQueueComponent.ERROR_SUMMARY_MAX) {
			return msg;
		}
		const lower = msg.toLowerCase();
		if (lower.includes('not enough quantity') || lower.includes('in inventory')) {
			const skuLike = msg.match(/\b[A-Z0-9]{6,12}\b/g);
			const uniq = skuLike ? [...new Set(skuLike)] : [];
			if (uniq.length > 2) {
				return `Quantity short (${uniq.length} SKUs) — click for details`;
			}
			if (uniq.length === 2) {
				return `Quantity short: ${uniq.join(', ')} — click for details`;
			}
			if (uniq.length === 1) {
				return `Quantity short: ${uniq[0]} — click for details`;
			}
			return 'Quantity short — click for details';
		}
		return msg.slice(0, 65).trim() + '… — click for details';
	}

	private getErrorCode(item: ExportQueueItem): string | undefined {
		const response = item.export_response as any;
		if (response && typeof response === 'object') {
			if (typeof response.error_code === 'string' && response.error_code) {
				return response.error_code;
			}
			if (typeof response.code === 'string' && response.code) {
				return response.code;
			}
		}
		return undefined;
	}

	/**
	 * Show the row refresh / menu “Retry export” for failed and dead-letter rows (server `tpl3pl/orders/retry` supports both).
	 * Unrecoverable error codes no longer hide the control — operators may retry after fixing the order.
	 */
	canQuickRetryExport(item: ExportQueueItem): boolean {
		if (!item?.orderno) {
			return false;
		}
		return item.export_status === 'failed' || item.export_status === 'dead_letter';
	}

	/** Whether the stored error code usually clears with a simple retry (for the error panel copy only). */
	exportFailureAppearsRecoverable(item: ExportQueueItem): boolean {
		if (item.export_status !== 'failed' && item.export_status !== 'dead_letter') {
			return false;
		}
		const unrecoverable = ['NO_ITEMS', 'STATUS_BLOCKED', 'STATUS_NOT_ELIGIBLE', 'STATUS_ALREADY_SHIPPED'];
		const errorCode = this.getErrorCode(item);
		if (errorCode && unrecoverable.indexOf(errorCode) !== -1) {
			return false;
		}
		return true;
	}

	openForceErpStatusDialog(item: ExportQueueItem): void {
		const statuses = this.orderStatusesForSelect;
		if (!statuses.length) {
			this.globalSearchService.showNotification('Order statuses not loaded yet — wait a moment and try again', 'warning', 'bottom', 'right');
			return;
		}
		if (!item?.orderno) {
			return;
		}
		this.dialog.open(ForceErpStatusDialogComponent, {
			width: '440px',
			maxWidth: '96vw',
			data: {
				orderno: item.orderno,
				currentStatusId: item.erp_order_status_id || '',
				statuses,
			} as ForceErpStatusDialogData,
		}).afterClosed().subscribe((r) => {
			if (r?.success && r.erp_order_status_id != null) {
				item.erp_order_status_id = r.erp_order_status_id;
				item.erp_order_status_name = r.erp_order_status_name != null ? r.erp_order_status_name : item.erp_order_status_name;
			}
		});
	}

	openErrorDetails(item: ExportQueueItem): void {
		if (!item.error_message && !item.export_response) {
			this.selectedErrorItem = null;
			this.selectedErrorDetails = null;
			return;
		}

		const errorCode = this.getErrorCode(item);
		const canRetry = this.exportFailureAppearsRecoverable(item);
		const response = item.export_response as any;

		this.selectedErrorItem = item;
		const skusNotFound = Array.isArray(response?.skus_not_found) ? (response.skus_not_found as string[]) : undefined;
		const outboundSkus = Array.isArray(response?.outbound_skus) ? (response.outbound_skus as string[]) : undefined;
		this.selectedErrorDetails = {
			message: item.error_message || (response && (response.message as string)) || '',
			errorCode,
			canRetry,
			response: response && (response.response ?? response.raw_response ?? response),
			circuitOpen: !!(response && response.circuit_open),
			skusNotFound,
			outboundSkus
		};
	}

	clearErrorDetails(): void {
		this.selectedErrorItem = null;
		this.selectedErrorDetails = null;
	}

	openResponseDetails(item: ExportQueueItem): void {
		if (!item) { return; }
		if (item.export_response) {
			this.dialog.open(RawResponseModalComponent, {
				width: '720px',
				maxWidth: '96vw',
				maxHeight: '90vh',
				data: {
					orderno: item.orderno,
					export_status: item.export_status,
					content: item.export_response,
					onSent: () => this.loadQueue(true),
					show_pre_export_reauth: true,
					override_cc_payment_gate: this.overrideCcPaymentGate,
					override_availability_gate: this.overrideAvailabilityGate
				},
				panelClass: 'raw-response-dialog'
			});
			return;
		}
		this.previewLoadingOrderNo = item.orderno;
		this.tpl3plService.getExportPreview(item.orderno).subscribe({
			next: (res) => {
				this.previewLoadingOrderNo = null;
				const bd = res.export_line_breakdown;
				const hasBreakdown = bd != null && Array.isArray(bd.lines);
				const hasPayload = res.success && res.request_payload != null;
				const hasPaymentPreview = res.export_preauth != null || res.export_cc_payment_gate != null;
				if (hasBreakdown || hasPayload || hasPaymentPreview) {
					this.dialog.open(RawResponseModalComponent, {
						width: '720px',
						maxWidth: '96vw',
						maxHeight: '90vh',
						data: {
							orderno: item.orderno,
							export_status: item.export_status,
							content: {
								request_payload: res.request_payload,
								response: !res.success ? { message: res.message, error_code: (res as { error_code?: string }).error_code } : undefined
							},
							export_line_breakdown: bd ?? null,
							export_preauth: res.export_preauth ?? null,
							export_cc_payment_gate: res.export_cc_payment_gate ?? null,
							customer_name_for_attach: item.customer_name,
							onSent: () => this.loadQueue(true),
							show_pre_export_reauth: true,
							override_cc_payment_gate: this.overrideCcPaymentGate,
							override_availability_gate: this.overrideAvailabilityGate
						},
						panelClass: 'raw-response-dialog'
					});
				} else {
					this.globalSearchService.showNotification(res.message || 'Could not load preview', 'warning', 'bottom', 'right');
				}
			},
			error: (err) => {
				this.previewLoadingOrderNo = null;
				this.globalSearchService.showNotification(err?.error?.message || 'Could not load preview', 'danger', 'bottom', 'right');
			}
		});
	}

	archiveQueueItem(item: ExportQueueItem): void {
		if (item.export_status !== 'exported') { return; }
		this.tpl3plService.archiveQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Order archived', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	unarchiveQueueItem(item: ExportQueueItem): void {
		this.tpl3plService.unarchiveQueueItem(item.id, item.orderno).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Order restored to exported state', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not unarchive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error unarchiving', 'danger', 'bottom', 'right')
		});
	}

	checkPgsStatus(item: ExportQueueItem): void {
		if (!item?.orderno) { return; }
		this.globalSearchService.showNotification('Fetching PGS status…', 'info', 'bottom', 'right');
		this.tpl3plService.getPgsOrderStatus(item.orderno).subscribe({
			next: (result: any) => {
				const content = result.success
					? { response: result.data, channel_used: result.channel_used }
					: { response: { message: result.message, b2c_error: result.b2c_error, b2b_error: result.b2b_error }, channel_used: result.channel_used };
				// Show a toast if the queue state changed as a result of the check.
				const oldState = result.old_state as string | null;
				const newState = result.new_state as string | null;
				if (oldState && newState && oldState !== newState) {
					this.globalSearchService.showNotification(
						'Queue state updated: ' + oldState + ' → ' + newState,
						'success',
						'bottom',
						'right'
					);
				}
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'open_orders',
						orderno: item.orderno,
						content,
					},
					panelClass: 'raw-response-dialog'
				}).afterClosed().subscribe(() => {
					// Reload so the table reflects the updated state / pgs_status_checked_at.
					this.loadQueue(true);
				});
			},
			error: (err) => {
				const msg = err?.error?.message || err?.message || 'Could not fetch PGS status';
				this.globalSearchService.showNotification(msg, 'danger', 'bottom', 'right');
				this.dialog.open(RawResponseModalComponent, {
					width: '720px',
					maxWidth: '96vw',
					maxHeight: '90vh',
					data: {
						context: 'open_orders',
						orderno: item.orderno,
						content: { response: { success: false, message: msg, error: err?.error } },
					},
					panelClass: 'raw-response-dialog'
				});
			}
		});
	}

	massUpdatePgsStatus(): void {
		if (this.massStatusUpdating) { return; }
		const batchSize = Math.max(1, Math.min(100, Number(this.pgsMassBatchSize) || 25));
		this.massStatusUpdating = true;
		this.massStatusResult = null;
		this.massStatusProgress = 'Starting…';

		const totals: { checked: number; updated: number; unchanged: number; failed: number; erp_status_updated: number; details: any[]; batch_count: number } = {
			checked: 0,
			updated: 0,
			unchanged: 0,
			failed: 0,
			erp_status_updated: 0,
			details: [],
			batch_count: 0
		};
		let offset = 0;
		const betweenBatchMs = 350;

		const runBatch = (): void => {
			this.tpl3plService.massUpdatePgsStatus(batchSize, offset).subscribe({
				next: (result) => {
					if (!result?.success) {
						this.massStatusUpdating = false;
						this.massStatusProgress = '';
						this.globalSearchService.showNotification(result?.message || 'PGS batch failed', 'danger', 'bottom', 'right');
						return;
					}
					totals.batch_count++;
					totals.checked += result.checked;
					totals.updated += result.updated;
					totals.unchanged += result.unchanged;
					totals.failed += result.failed;
					totals.erp_status_updated += result.erp_status_updated ?? 0;
					if (result.details?.length) {
						totals.details.push(...result.details);
					}
					this.massStatusProgress = `Processed ${totals.checked} order(s) in ${totals.batch_count} batch(es)…`;

					const hasMore = !!result.has_more && result.checked > 0;
					if (hasMore) {
						offset += result.checked;
						window.setTimeout(() => runBatch(), betweenBatchMs);
						return;
					}

					this.massStatusUpdating = false;
					this.massStatusProgress = '';
					this.massStatusResult = totals;
					const erpPart = totals.erp_status_updated > 0 ? `, ${totals.erp_status_updated} ERP status(es) updated` : '';
					const msg = `PGS status refreshed: ${totals.checked} checked (${totals.batch_count} batch(es)), ${totals.updated} queue state(s) updated${erpPart}, ${totals.failed} failed`;
					this.globalSearchService.showNotification(msg, totals.failed > 0 ? 'warning' : 'success', 'bottom', 'right');
					if (totals.updated > 0 || totals.erp_status_updated > 0) {
						this.loadQueue(true);
					}
				},
				error: (err) => {
					this.massStatusUpdating = false;
					this.massStatusProgress = '';
					this.globalSearchService.showNotification(err?.error?.message || 'Mass status update failed', 'danger', 'bottom', 'right');
				}
			});
		};

		runBatch();
	}

	archiveAllExported(): void {
		this.tpl3plService.archiveAllExported().subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification(result.message || 'Exported orders archived', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	archivePgsShippedQueue(includeInvoiced: boolean): void {
		this.tpl3plService.archivePgsShippedQueue(includeInvoiced).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification(result.message || 'Archived', 'success', 'bottom', 'right');
					this.loadQueue(true);
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not archive', 'danger', 'bottom', 'right');
				}
			},
			error: (err) => this.globalSearchService.showNotification(err?.error?.message || 'Error archiving', 'danger', 'bottom', 'right')
		});
	}

	viewLogs(item: ExportQueueItem): void {
		this.navigateToTab('logs', item.orderno);
	}

	viewShipments(item: ExportQueueItem): void {
		this.navigateToTab('shipments', item.orderno);
	}

	private navigateToTab(tab: 'logs' | 'shipments', orderno: string): void {
		this.router.navigate(['../' + tab], { relativeTo: this.route, queryParams: { orderno } });
	}

	// ── Per-row actions menu (shared mat-menu) ──────────────────────────────────

	runRowMenuRetryExport(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.retryExport(item);
		}
	}

	runRowMenuExportNow(): void {
		const item = this.exportQueueRowMenuItem;
		if (item?.orderno) {
			this.exportOrder(item.orderno);
		}
	}

	runRowMenuSplitBackorder(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.openSplitBackorderDialog(item);
		}
	}

	runRowMenuCancelAndSplitPreview(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.openCancelAndSplitBackorderDialog(item);
		}
	}

	runRowMenuSplitUninvoicedOpenLines(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.openSplitUninvoicedOpenLinesDialog(item);
		}
	}

	runRowMenuPrepareSplitResubmit(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.prepareSplitResubmit(item);
		}
	}

	runRowMenuBumpOrderno(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.bumpOrdernoFor3plResubmit(item);
		}
	}

	runRowMenuForceResend(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.forceResendExported(item);
		}
	}

	runRowMenuCancel3pl(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.cancelOrder(item);
		}
	}

	runRowMenuCancelWarehouseOnly(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.cancelAtWarehouseOnly(item);
		}
	}

	runRowMenuViewRaw(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.openResponseDetails(item);
		}
	}

	runRowMenuViewLogs(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.viewLogs(item);
		}
	}

	runRowMenuViewShipments(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.viewShipments(item);
		}
	}

	runRowMenuCheckPgs(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.checkPgsStatus(item);
		}
	}

	runRowMenuArchive(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.archiveQueueItem(item);
		}
	}

	runRowMenuUnarchive(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.unarchiveQueueItem(item);
		}
	}

	runRowMenuDelete(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.deleteQueueItem(item);
		}
	}

	runRowMenuMarkDispatched(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.markQueueDispatchedFromFailure(item);
		}
	}

	deleteQueueItem(item: ExportQueueItem): void {
		this.tpl3plService.deleteQueueItem(item.id).subscribe({
			next: (result) => {
				if (result.success) {
					this.globalSearchService.showNotification('Queue item removed', 'success', 'bottom', 'right');
					this.allItems = this.allItems.filter(i => i.id !== item.id);
					this.applyFilters();
					this.refreshQueueStats();
					if (this.selectedErrorItem?.id === item.id) {
						this.clearErrorDetails();
					}
				} else {
					this.globalSearchService.showNotification(result.message || 'Could not remove item', 'danger', 'bottom', 'right');
				}
			},
			error: (error) => {
				this.globalSearchService.showNotification(error?.error?.message || 'Error removing queue item', 'danger', 'bottom', 'right');
			}
		});
	}

	viewOrder(orderno: string): void {
		if (!orderno) { return; }
		this.dialog.open(OrderDetailDialogComponent, {
			data: { orderno },
			width: '90vw',
			maxWidth: '1100px',
			maxHeight: '90vh',
			panelClass: 'order-detail-panel',
		});
	}

	/** Saved profile card → order + pre-export pre-auth (when rep skipped payment). */
	canAttachCardFromQueue(item: ExportQueueItem): boolean {
		if (!item?.orderno || item.export_status === 'dead_letter') {
			return false;
		}
		return true;
	}

	openAttachCardForQueue(item: ExportQueueItem): void {
		if (!item?.orderno) {
			return;
		}
		const cust = String(item.customer_name || '').trim();
		this.dialog
			.open(AttachSavedCardToOrderDialogComponent, {
				width: '460px',
				maxWidth: '96vw',
				data: {
					orderno: String(item.orderno),
					customerName: cust,
					tpl3plService: this.tpl3plService,
					runPreauthAfterAttach: true,
				},
			})
			.afterClosed()
			.subscribe((r) => {
				if (r?.success) {
					if (r.message) {
						this.globalSearchService.showNotification(
							r.message,
							r.preauth_ok === false ? 'warning' : 'success',
							'bottom',
							'right'
						);
					}
					this.loadQueue(true);
				}
			});
	}

	runRowMenuAttachCardPreauth(): void {
		const item = this.exportQueueRowMenuItem;
		if (item) {
			this.openAttachCardForQueue(item);
		}
	}
}
